import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { MenuService, MenuProduct } from '../services/MenuService.ts';
import { useFuzzySearch } from '../hooks/useFuzzySearch.ts';
import { useDebounce } from '../hooks/useDebounce.ts';
import { useCart } from '../contexts/CartContext.tsx';
import { useProducts } from '../contexts/ProductsContext.tsx';
import { useDeals } from '../hooks/useDeals.ts';
import DealsDisplay from './DealsDisplay.tsx';
import ProductDetailDrawer from './ProductDetailDrawer.tsx';
import ProductTileV3 from './ProductTileV3.tsx';
import HomeCollections from './HomeCollections.tsx';
import analyticsService from '../services/AnalyticsService.ts';
import AttributionService from '../services/AttributionService.ts';
import CustomerJourneyService from '../services/CustomerJourneyService.ts';
import BusinessIntelligenceService from '../services/BusinessIntelligenceService.ts';
import FrustrationDetectionService from '../services/FrustrationDetectionService.ts';
import { MENU_ID_TO_STORE } from '../config/storeMenuConfig.ts';
import { getPublishedCollections } from '../services/homeCollectionsApiService.ts';
import { getPopupAlertForStore } from '../services/popupAlertApiService.ts';
import PopupAlertModal from './PopupAlertModal.tsx';
import { wouldExceedInfusedLimit, INFUSED_LIMIT_GRAMS } from '../utils/marylandInfusedLimit.ts';
import './DutchieMenu.css';
// Use require for images to avoid TypeScript errors
const magnifyingGlass = require('../magnifying_new_revolutionary.png');
const chevronDown = require('./chevron-down-01.png');

const { useEffect, useState, useMemo, useCallback, useRef } = React;  

// Searchable properties configuration - easy to modify which fields are searchable
const SEARCHABLE_KEYS = [
  'name',
  'description', 
  'brand.name',
  'category',
  'subcategory',
  'strainType',
  'effects'
];

// Map terpene technical names to display names
const getTerpeneDisplayName = (terpeneName: string | null): string => {
  if (!terpeneName) return '';
  
  // Normalize terpene name by converting β to Beta and removing spaces/hyphens
  const normalizedName = terpeneName
    .replace(/β-?/g, 'Beta')           // Convert β or β- to Beta
    .replace(/\s+/g, '')              // Remove spaces
    .replace(/-/g, '');               // Remove hyphens
  
  const displayNameMap: { [key: string]: string } = {
    'AlphaPinene': 'α-Pinene',
    'BetaCaryophyllene': 'β-Caryophyllene',
    'BetaEudesmol': 'β-Eudesmol',
    'BetaMyrcene': 'β-Myrcene',
    'BetaPinene': 'β-Pinene',
    'Bisabolol': 'Bisabolol',
    'CaryophylleneOxide': 'Caryophyllene Oxide',
    'Eucalyptol': 'Eucalyptol',
    'Humulene': 'Humulene',
    'Limonene': 'Limonene',
    'Linalool': 'Linalool',
    'Terpinolene': 'Terpinolene',
    'TransNerolidol': 'Trans-Nerolidol'
  };
  
  // Try normalized name first, then fall back to original
  return displayNameMap[normalizedName] || displayNameMap[terpeneName] || terpeneName;
};

// Regex to filter out THC-related cannabinoids (case-insensitive, flexible spacing and formatting)
const THC_FILTER_REGEX = /\bTHC(?:\s*[-_]?\s*[A-Z]?\d*(?:\.\d+)?[A-Z]*)*\b/i;

// Helper function to check if a cannabinoid is THC-related and should be filtered out
const isTHCRelatedCannabinoid = (cannabinoidName: string): boolean => {
  return THC_FILTER_REGEX.test(cannabinoidName);
};

// Helper function to check if a value is effectively zero (handles 0, 0.0, and floating point precision issues)
const isEffectivelyZero = (value: number | null | undefined): boolean => {
  return value === null || value === undefined || Math.abs(value) < 0.005;
};

// Helper function to format strain type with proper capitalization
const formatStrainType = (strainType: string): string => {
  if (!strainType) return '';
  if (strainType === 'NOT_APPLICABLE') return '';
  if (strainType === 'ONE_TO_ONE') return '1:1';
  if (strainType === 'SATIVA_HYBRID') return 'Sativa-Hybrid';
  if (strainType === 'INDICA_HYBRID') return 'Indica-Hybrid';
  if (strainType === 'HIGH_CBD') return 'High CBD';

  // Handle underscore-separated values generically
  if (strainType.includes('_')) {
    return strainType.split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  return strainType.toLowerCase().replace(/^\w/, c => c.toUpperCase());
};

// Helper function to format lineage for display in filters
const formatLineageForDisplay = (lineage: string): string => {
  return formatStrainType(lineage);
};

// Helper function to parse mg value from formatted string (e.g. "100mg" -> 100)
const parseMgValue = (formatted: string | undefined): number => {
  if (!formatted) return 0;
  const match = formatted.match(/(\d+(?:\.\d+)?)\s*mg/i);
  return match ? parseFloat(match[1]) : 0;
};

// Helper function to check if edible exceeds recreational THC limit
const exceedsRecreationalThcLimit = (product: MenuProduct): boolean => {
  if (product.category?.toLowerCase() !== 'edibles') {
    return false; // Only applies to edibles
  }
  
  if (product.potencyThc?.unit === 'mg' && product.potencyThc?.formatted) {
    const thcValue = parseMgValue(product.potencyThc.formatted);
    return thcValue > 100; // Recreational limit is 100mg
  }
  
  return false;
};

// Helper function to get cannabinoid display name (simplify technical names)
const getCannabinoidDisplayName = (cannabinoidName: string | null): string => {
  if (!cannabinoidName) return '';
  
  // Extract simple name from full technical name
  // e.g., "THC-D9 (Delta 9–tetrahydrocannabinol)" -> "THC-D9"
  const match = cannabinoidName.match(/^([^(]+)/);
  const simpleName = match ? match[1].trim() : cannabinoidName;
  
  // Map common cannabinoids to preferred display names
  const displayNameMap: { [key: string]: string } = {
    'CBD (Cannabidiol)': 'CBD',
    'CBDA (Cannabidiolic acid)': 'CBDA',
    'CBG (Cannabigerol)': 'CBG',
    'CBN (Cannabinol)': 'CBN',
    'THC-D9 (Delta 9–tetrahydrocannabinol)': 'THC-D9',
    'THCA (Δ9-tetrahydrocannabinolic acid)': 'THCA',
    'CBC (Cannabichromene)': 'CBC',
    'CBGA (Cannabigerolic acid)': 'CBGA',
    'THCV (Tetrahydrocannabivarin)': 'THCV',
    'CBDV (Cannabidivarin)': 'CBDV'
  };
  
  return displayNameMap[cannabinoidName] || simpleName;
};

// Helper function to check if a product contains any of the selected terpenes
const productContainsAnyTerpene = (product: MenuProduct, selectedTerpenes: string[]): boolean => {
  if (!product.terpenes || product.terpenes.length === 0 || selectedTerpenes.length === 0) return false;
  
  return product.terpenes.some(terpene => 
    terpene.name && 
    !isEffectivelyZero(terpene.value) &&
    selectedTerpenes.includes(getTerpeneDisplayName(terpene.name))
  );
};

// Helper function to check if a product contains any of the selected cannabinoids
const productContainsAnyCannabinoid = (product: MenuProduct, selectedCannabinoids: string[]): boolean => {
  if (!product.cannabinoids || product.cannabinoids.length === 0 || selectedCannabinoids.length === 0) return false;
  
  return product.cannabinoids.some(cannabinoid => {
    if (!cannabinoid.name || isEffectivelyZero(cannabinoid.value)) return false;
    
    const productCannabinoidDisplayName = getCannabinoidDisplayName(cannabinoid.name);
    
    return selectedCannabinoids.includes(productCannabinoidDisplayName);
  });
};

// Helper to find any cart item for a product
const findCartItemForProduct = (product: MenuProduct, cart: any): any => {
  if (!cart || !cart.items || !product) {
    return null;
  }
  return cart.items.find((item: any) => item.productId === product.id);
};

// Smart increment helpers - shared logic for both grid and detail drawer
const parseWeightFromOption = (option: string): number | null => {
  if (!option) return null;
  
  const cleanOption = option.toLowerCase().trim();
  
  // Direct gram parsing (e.g., "1g", "3.5g", "1", "3.5")
  const gramMatch = cleanOption.match(/^(\d+(?:\.\d+)?)g?$/);
  if (gramMatch) {
    return parseFloat(gramMatch[1]);
  }
  
  // Fractional oz parsing (e.g., "1/8oz", "1/8", "1/4oz", "1/2oz", "1oz")
  const ozMatch = cleanOption.match(/^(\d+)\/(\d+)(?:oz)?$/) || cleanOption.match(/^(\d+)(?:oz)?$/);
  if (ozMatch) {
    if (ozMatch[2]) {
      // Fraction like "1/8"
      const numerator = parseFloat(ozMatch[1]);
      const denominator = parseFloat(ozMatch[2]);
      return (numerator / denominator) * 28; // 1oz = 28g
    } else {
      // Whole number like "1oz"
      return parseFloat(ozMatch[1]) * 28;
    }
  }
  
  return null;
};

// NEW: Get sorted list of variants by weight for Vermont cycling
const getSortedVariants = (product: MenuProduct): any[] => {
  if (!product.variants || product.variants.length === 0) {
    return [];
  }
  
  return [...product.variants].sort((a, b) => {
    const weightA = a.flowerEquivalent?.value || parseWeightFromOption(a.option) || 0;
    const weightB = b.flowerEquivalent?.value || parseWeightFromOption(b.option) || 0;
    return weightA - weightB;
  });
};

// NEW: Get current variant from cart for Vermont
const getCurrentVariantFromCart = (product: MenuProduct, cart: any): any => {
  const cartItem = findCartItemForProduct(product, cart);
  if (!cartItem || !cartItem.option) return null;
  
  // Find the variant that matches the cart item's option
  return product.variants?.find(v => v.option === cartItem.option) || null;
};

// NEW: Get next variant in sequence for Vermont
const getNextVariant = (product: MenuProduct, currentVariant: any): any => {
  const sortedVariants = getSortedVariants(product);
  if (sortedVariants.length === 0) return null;
  
  if (!currentVariant) {
    return sortedVariants[0]; // Start with smallest variant
  }
  
  const currentIndex = sortedVariants.findIndex(v => v.id === currentVariant.id);
  
  // If we're at the last variant, stay there
  if (currentIndex === sortedVariants.length - 1) {
    return sortedVariants[currentIndex];
  }
  
  // Otherwise, return next variant
  return currentIndex >= 0 && currentIndex < sortedVariants.length - 1
    ? sortedVariants[currentIndex + 1]
    : sortedVariants[sortedVariants.length - 1];
};

// NEW: Get previous variant in sequence for Vermont
const getPreviousVariant = (product: MenuProduct, currentVariant: any): any => {
  const sortedVariants = getSortedVariants(product);
  if (sortedVariants.length === 0 || !currentVariant) return null;
  
  const currentIndex = sortedVariants.findIndex(v => v.id === currentVariant.id);
  
  // If we're at the first variant, return null (which means remove from cart)
  if (currentIndex === 0) {
    return null;
  }
  
  // Otherwise, return previous variant
  return currentIndex > 0
    ? sortedVariants[currentIndex - 1]
    : null;
};

// Component to display resolved reward details
const RewardDetails: React.FC<{ rewardIds: string[] }> = ({ rewardIds }) => {
  const [resolvedRewards, setResolvedRewards] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!rewardIds || rewardIds.length === 0) {
      setResolvedRewards([]);
      return;
    }

    const resolveRewards = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const rewards = await MenuService.resolveRewards(rewardIds);
        setResolvedRewards(rewards);
      } catch (err) {
        console.error('Failed to resolve rewards:', err);
        setError(err instanceof Error ? err.message : 'Failed to resolve rewards');
      } finally {
        setIsLoading(false);
      }
    };

    resolveRewards();
  }, [rewardIds]);

  if (!rewardIds || rewardIds.length === 0) {
    return <span>N/A</span>;
  }

  if (isLoading) {
    return <span>Loading rewards...</span>;
  }

  if (error) {
    return <span style={{ color: 'orange' }}>Error: {error}</span>;
  }

  if (resolvedRewards.length === 0) {
    return <span>{rewardIds.join(', ')} (No details found)</span>;
  }

  return (
    <div>
      <div>IDs: {rewardIds.join(', ')}</div>
      {resolvedRewards.map((reward, index) => (
        <div key={reward.id || index} style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f0f8ff', borderLeft: '3px solid #4CAF50' }}>
          <div style={{ fontWeight: 'bold', fontSize: '1.1em', marginBottom: '4px' }}>
            {reward.menuDisplayConfiguration?.name || reward.name}
          </div>
          
          <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '4px' }}>
            Type: <span style={{ fontWeight: 'bold' }}>{reward.type}</span> | 
            Menu: <span style={{ fontWeight: 'bold' }}>{reward.menuType || 'N/A'}</span>
            {reward.redemptionLimit && ` | Limit: ${reward.redemptionLimit}`}
          </div>

          {reward.menuDisplayConfiguration?.description && (
            <div style={{ fontSize: '0.85em', color: '#555', fontStyle: 'italic', marginBottom: '4px' }}>
              "{reward.menuDisplayConfiguration.description}"
            </div>
          )}

          {reward.emailConfiguration?.description && (
            <div style={{ fontSize: '0.85em', color: '#333', marginBottom: '4px' }}>
              <strong>Details:</strong> {reward.emailConfiguration.description}
            </div>
          )}

          {reward.scheduleConfiguration && (
            <div style={{ fontSize: '0.8em', color: '#777', marginTop: '4px' }}>
              {reward.scheduleConfiguration.startStamp && (
                <div>Start: {new Date(reward.scheduleConfiguration.startStamp).toLocaleDateString()}</div>
              )}
              {reward.scheduleConfiguration.endStamp && (
                <div>End: {new Date(reward.scheduleConfiguration.endStamp).toLocaleDateString()}</div>
              )}
              {reward.scheduleConfiguration.recurringStartTime && reward.scheduleConfiguration.recurringEndTime && (
                <div>
                  Time: {reward.scheduleConfiguration.recurringStartTime} - {reward.scheduleConfiguration.recurringEndTime}
                </div>
              )}
              {reward.scheduleConfiguration.days && reward.scheduleConfiguration.days.length > 0 && (
                <div>
                  Days: {reward.scheduleConfiguration.days.map(day => 
                    ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day]
                  ).join(', ')}
                </div>
              )}
            </div>
          )}

          {reward.menuDisplayConfiguration?.image && (
            <div style={{ marginTop: '4px' }}>
              <img 
                src={reward.menuDisplayConfiguration.image} 
                alt={reward.name}
                style={{ maxWidth: '100px', maxHeight: '50px', objectFit: 'cover' }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Helper function to check if a product has any rewards
const hasRewards = (product: MenuProduct): boolean => {
  return product.variants.some(variant => 
    variant.specialRewardIds && variant.specialRewardIds.length > 0
  );
};

// Helper function to check if a product has special prices
const hasSpecialPrices = (product: MenuProduct): boolean => {
  return product.variants.some(variant => 
    variant.specialPriceMed !== null || variant.specialPriceRec !== null
  );
};

// Helper function to check if a product has any deals (rewards or special prices)
const hasDeals = (product: MenuProduct): boolean => {
  return hasRewards(product) || hasSpecialPrices(product);
};

// Helper function to calculate discount percentage
const calculateDiscount = (regularPrice: number, specialPrice: number): number => {
  if (regularPrice <= 0) return 0;
  return Math.round(((regularPrice - specialPrice) / regularPrice) * 100);
};

// Helper function to calculate total terpene percentage
const getTotalTerpenePercentage = (product: MenuProduct): number => {
  if (!product.terpenes || product.terpenes.length === 0) return 0;
  
  return product.terpenes.reduce((total, terpene) => {
    if (terpene.value && !isEffectivelyZero(terpene.value)) {
      return total + terpene.value;
    }
    return total;
  }, 0);
};

// Helper function to check if total terpene data is valid for display
const hasValidTerpeneData = (product: MenuProduct): boolean => {
  const totalTerpenes = getTotalTerpenePercentage(product);
  return totalTerpenes > 0;
};

// Helper function to sanitize brand names
const sanitizeBrandName = (brandName: string): string => {
  if (!brandName || typeof brandName !== 'string') return '';
  
  const sanitized = brandName
    .trim() // Remove leading/trailing spaces
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/[\r\n\t]/g, ' ') // Replace line breaks and tabs with spaces
    .trim(); // Trim again after replacements
  
  // Return empty string if the result is just whitespace or empty
  return sanitized || '';
};

// Helper function to get display name for categories
const getCategoryDisplayName = (categoryName: string): string => {
  if (!categoryName) return categoryName;
  if (categoryName === 'home') return 'HOME';
  
  // Define the specific display mapping - handle various possible API category names
  const categoryDisplayMap: { [key: string]: string } = {
    'flower': 'FLOWER',
    'flowers': 'FLOWER',
    'pre-rolls': 'PRE-ROLLS', // This is our normalized form
    'vaporizer': 'VAPES',
    'vaporizers': 'VAPES',
    'vape': 'VAPES',
    'vapes': 'VAPES',
    'cartridge': 'VAPES',
    'cartridges': 'VAPES',
    'edible': 'EDIBLES',
    'edibles': 'EDIBLES',
    'concentrate': 'CONCENTRATES',
    'concentrates': 'CONCENTRATES',
    'topical': 'TOPICALS',
    'topicals': 'TOPICALS',
    'accessory': 'ACCESSORIES',
    'accessories': 'ACCESSORIES'
  };
  
  const lowerCaseName = categoryName.toLowerCase().trim();
  return categoryDisplayMap[lowerCaseName] || categoryName.toUpperCase();
};

// Helper function to sanitize category names
// Replaces special characters, converts - and _ to spaces
const sanitizeCategoryName = (categoryName: string): string => {
  if (!categoryName) return categoryName;
  if (categoryName === 'home') return 'Home';
  
  return categoryName
    .trim()
    .replace(/[^\w\s-_]/g, '') // Remove special characters except - and _
    .replace(/[-_]/g, ' ') // Replace - and _ with spaces
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' '); // Capitalize each word
};

// Helper function to get potency value from product
const getPotencyValue = (product: MenuProduct, type: 'thc' | 'cbd'): number => {
  const potencyObj = type === 'thc' ? product.potencyThc : product.potencyCbd;
  
  // If we have a range with values, use the maximum value
  if (potencyObj.range && potencyObj.range.length > 0) {
    return Math.max(...potencyObj.range.map(val => parseFloat(val)));
  }
  
  // Try to extract from formatted string (e.g. "18.5%" -> 18.5)
  if (potencyObj.formatted) {
    const match = potencyObj.formatted.match(/(\d+\.?\d*)/);
    if (match && match[1]) {
      return parseFloat(match[1]);
    }
  }
  
  // If no valid potency info, return 0
  return 0;
};

// Helper function to extract unique mg-based potency values from all products
const getUniqueMgPotencyValues = (products: MenuProduct[]) => {
  const thcValues = new Set<string>();
  const cbdValues = new Set<string>();
  const cannabinoidValues: { [key: string]: Set<string> } = {};
  
  products.forEach(product => {
    // Handle existing potencyThc and potencyCbd fields
    if (product.potencyThc?.formatted && product.potencyThc?.unit === 'mg') {
      thcValues.add(product.potencyThc.formatted);
    }
    if (product.potencyCbd?.formatted && product.potencyCbd?.unit === 'mg') {
      cbdValues.add(product.potencyCbd.formatted);
    }
    
    // Handle cannabinoids array - only for edibles
    if (product.category?.toLowerCase() === 'edibles' && product.cannabinoids) {
      product.cannabinoids.forEach(cannabinoid => {
        if (cannabinoid.name && cannabinoid.value && cannabinoid.unitSymbol === 'MILLIGRAMS' && !isEffectivelyZero(cannabinoid.value)) {
          const displayName = getCannabinoidDisplayName(cannabinoid.name);
          const formattedValue = `${cannabinoid.value}mg`;
          
          if (!cannabinoidValues[displayName]) {
            cannabinoidValues[displayName] = new Set<string>();
          }
          cannabinoidValues[displayName].add(formattedValue);
        }
      });
    }
  });
  
  // Sort numerically by extracting the number from formatted values like "100mg"
  const sortValues = (values: Set<string>) => {
    return Array.from(values).sort((a, b) => {
      const aNum = parseInt(a.replace('mg', ''));
      const bNum = parseInt(b.replace('mg', ''));
      return aNum - bNum;
    });
  };
  
  const sortedThc = sortValues(thcValues);
  const sortedCbd = sortValues(cbdValues);
  
  // Sort cannabinoid values
  const sortedCannabinoids: { [key: string]: string[] } = {};
  Object.keys(cannabinoidValues).forEach(cannabinoid => {
    sortedCannabinoids[cannabinoid] = sortValues(cannabinoidValues[cannabinoid]);
  });
  
  return { thc: sortedThc, cbd: sortedCbd, cannabinoids: sortedCannabinoids };
};

// Helper function to check if there are any products with mg-based potency
const hasAnyMgBasedPotency = (products: MenuProduct[]): boolean => {
  return products.some(product => 
    (product.potencyThc?.unit === 'mg' && product.potencyThc?.formatted) ||
    (product.potencyCbd?.unit === 'mg' && product.potencyCbd?.formatted) ||
    (product.category?.toLowerCase() === 'edibles' && product.cannabinoids?.some(cannabinoid => 
      cannabinoid.name && cannabinoid.value && cannabinoid.unitSymbol === 'MILLIGRAMS' && !isEffectivelyZero(cannabinoid.value)
    ))
  );
};

// Helper function to check if weight should be displayed for a product
const shouldDisplayWeight = (product: MenuProduct): boolean => {
  if (!product || !product.category) return false;
  
  // Don't show weight for edibles, accessories, or topicals
  const sanitizedCategory = sanitizeCategoryName(product.category).toLowerCase();
  const excludedCategories = ['edible', 'edibles', 'accessory', 'accessories', 'topical', 'topicals'];
  
  for (const excludedCategory of excludedCategories) {
    if (sanitizedCategory === excludedCategory || sanitizedCategory.includes(excludedCategory)) {
      return false;
    }
  }
  
  return true;
};

// Helper function to format weight values with leading zeros and normalize format
const formatWeight = (weight: string): string => {
  if (!weight || weight === 'all') return weight;

  // Trim whitespace and convert to lowercase for consistent comparison
  let normalized = weight.trim().toLowerCase();

  // If the weight starts with a decimal point, add leading zero
  if (normalized.startsWith('.')) {
    normalized = '0' + normalized;
  }

  // Normalize "oz" suffix for fractional ounces (remove spaces and "oz")
  // This ensures "1/8oz", "1/8 oz", and "1/8" all match
  normalized = normalized.replace(/\s*oz$/i, '');

  return normalized;
};

const formatSubcategory = (subcategory: string): string => {
  if (!subcategory) return '';

  // Replace underscores with spaces and convert to title case
  return subcategory
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to check if a weight value is parseable
const isValidWeight = (weight: string): boolean => {
  if (!weight || weight === 'all') return true;

  // Accept any parseable numeric weight (e.g., "0.3g", "0.5g", "1g")
  const numericMatch = weight.match(/^(\d*\.?\d+)/);
  return numericMatch !== null;
};

// Skeleton Loader Component V3 - Matches ProductTileV3 layout
const ProductSkeletonV3 = ({ shimmerFadeOut, showProgress, isMobile }: {
  shimmerFadeOut: boolean;
  showProgress: boolean;
  isMobile: boolean;
}) => (
  <div className={`skeleton-tile-v3 ${isMobile ? 'mobile-layout' : ''} ${showProgress ? 'loading-progress' : ''} ${shimmerFadeOut ? 'fade-out' : ''}`}>
    {/* Image Section */}
    <div className={`skeleton-image-v3 ${shimmerFadeOut ? 'shimmer-fade-out' : ''}`}>
      <div className="skeleton-deal-triangle"></div>
    </div>

    {/* Details Section */}
    <div className="skeleton-details-v3">
      <div className="skeleton-details-content-v3">
        {/* Left: Text rows */}
        <div className="skeleton-text-v3">
          <div className="skeleton-row skeleton-name"></div>
          <div className="skeleton-row skeleton-brand"></div>
          <div className="skeleton-row skeleton-potency"></div>
          <div className="skeleton-row skeleton-weight"></div>
        </div>
        {/* Right: Badges (desktop only, mobile handled separately) */}
        {!isMobile && (
          <div className="skeleton-badges-v3">
            <div className="skeleton-badge"></div>
            <div className="skeleton-badge"></div>
          </div>
        )}
      </div>
    </div>

    {/* Badge area - mobile only (bottom left) */}
    {isMobile && (
      <div className="skeleton-badge-area-mobile">
        <div className="skeleton-badge"></div>
      </div>
    )}

    {/* Button Section */}
    <div className="skeleton-button-v3">
      <div className="skeleton-button-pill"></div>
    </div>
  </div>
);

/** Simple hash for popup alert sessionStorage keys */
function simpleHash(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
  }
  return Math.abs(hash).toString(36);
}

function DutchieMenu({ pricingType: initialPricingType = 'MEDICAL', menuId, legacyMenuId }: { pricingType?: 'MEDICAL' | 'RECREATIONAL', menuId?: string, legacyMenuId?: string }) {
  // URL parameter handling for deep linking
  const [searchParams, setSearchParams] = useSearchParams();

  // Simple render counter
  const renderCount = useRef(0);
  renderCount.current += 1;

  const { addToCart, updateQuantity, removeFromCart, isLoading: cartLoading, error: cartError, cart, toggleCart, setRetailerId } = useCart();
  const { setProducts: setContextProducts } = useProducts();
  const attributionService = AttributionService.getInstance();
  const journeyService = CustomerJourneyService.getInstance();
  const businessIntelligenceService = BusinessIntelligenceService.getInstance();
  const frustrationService = FrustrationDetectionService.getInstance();
  const [products, setProducts] = useState<MenuProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isInventoryError, setIsInventoryError] = useState(false);
  const [dismissedErrorAt, setDismissedErrorAt] = useState(0);
  const [popupAlert, setPopupAlert] = useState<{ message: string } | null>(null);
  const [popupAlertAcknowledged, setPopupAlertAcknowledged] = useState(false);
  const [productsJustLoaded, setProductsJustLoaded] = useState(false);
  const [showCascadeAnimations, setShowCascadeAnimations] = useState(false);
  const [showCartAnimation, setShowCartAnimation] = useState(false);
  const [shimmerFadeOut, setShimmerFadeOut] = useState(false);
  const [showSkeletonReveal, setShowSkeletonReveal] = useState(false);
  const [loadingStartTime, setLoadingStartTime] = useState<number>(Date.now());
  const [showLoadingProgress, setShowLoadingProgress] = useState(false);
  // Initialize state from URL parameters (deep linking support)
  const [selectedProduct, setSelectedProduct] = useState<MenuProduct | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>(() => {
    // Support both 'category' (singular) and 'categories' (plural) params
    const categoriesParam = searchParams.get('categories');
    const categoryParam = searchParams.get('category');
    if (categoriesParam) {
      // Multiple categories from deep link - use first one for UI, but filtering uses selectedCategories
      const cats = categoriesParam.split(',').map(c => c.trim()).filter(Boolean);
      return cats[0] || 'home';
    }
    // Backward compat: ?category=all maps to home
    return categoryParam === 'all' ? 'home' : (categoryParam || 'home');
  });
  // Multi-category support for deep links (e.g., ?categories=ACCESSORIES,EDIBLES)
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
    const categoriesParam = searchParams.get('categories');
    if (categoriesParam) {
      const cats = categoriesParam.split(',').map(c => c.trim()).filter(Boolean);
      return cats;
    }
    // Fallback to singular category param
    const categoryParam = searchParams.get('category');
    if (categoryParam && categoryParam !== 'home') {
      return [categoryParam];
    }
    return [];
  });
  const [selectedWeights, setSelectedWeights] = useState<string[]>(() => {
    const weightsParam = searchParams.get('weights');
    const weights = weightsParam ? weightsParam.split(',').map(w => formatWeight(w.trim())) : [];
    return weights;
  });
  const [isWeightCollapsed, setIsWeightCollapsed] = useState<boolean>(true);
  const [selectedBrands, setSelectedBrands] = useState<string[]>(() => {
    const brandsParam = searchParams.get('brands');
    const brands = brandsParam ? brandsParam.split(',').map(b => decodeURIComponent(b)) : [];
    return brands;
  });
  const [isBrandCollapsed, setIsBrandCollapsed] = useState<boolean>(true);
  const [selectedLineages, setSelectedLineages] = useState<string[]>(() => {
    const lineagesParam = searchParams.get('lineages');
    const lineages = lineagesParam ? lineagesParam.split(',') : [];
    return lineages;
  });
  const [isLineageCollapsed, setIsLineageCollapsed] = useState<boolean>(true);
  const [selectedTerpenes, setSelectedTerpenes] = useState<string[]>(() => {
    const terpenesParam = searchParams.get('terpenes');
    const terpenes = terpenesParam ? terpenesParam.split(',') : [];
    return terpenes;
  });
  const [isTerpeneCollapsed, setIsTerpeneCollapsed] = useState<boolean>(true);
  const [selectedCannabinoids, setSelectedCannabinoids] = useState<string[]>(() => {
    const cannabinoidsParam = searchParams.get('cannabinoids');
    const cannabinoids = cannabinoidsParam ? cannabinoidsParam.split(',') : [];
    return cannabinoids;
  });
  const [isCannabinoidCollapsed, setIsCannabinoidCollapsed] = useState<boolean>(true);
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>(() => {
    const subcategoriesParam = searchParams.get('subcategories');
    const subcategories = subcategoriesParam ? subcategoriesParam.split(',') : [];
    return subcategories;
  });
  const [isSubcategoryCollapsed, setIsSubcategoryCollapsed] = useState<boolean>(true);

  // Special/Deal filter - when present, only show products in the specified deal
  const [specialId, setSpecialId] = useState<string | null>(() => {
    const specialIdParam = searchParams.get('specialId');
    return specialIdParam;
  });

  // Multi-product filter - when ?productIds=id1,id2,id3 is in the URL, the menu
  // narrows to that curated list and preserves the order the admin chose.
  const [multiProductIds, setMultiProductIds] = useState<string[]>(() => {
    const param = searchParams.get('productIds');
    return param ? param.split(',').map(s => s.trim()).filter(Boolean) : [];
  });
  // Keep state in sync with URL changes (e.g. user clicks another banner)
  useEffect(() => {
    const param = searchParams.get('productIds');
    const next = param ? param.split(',').map(s => s.trim()).filter(Boolean) : [];
    setMultiProductIds(prev => {
      if (prev.length === next.length && prev.every((id, i) => id === next[i])) return prev;
      return next;
    });
  }, [searchParams]);
  const [specialProductIds, setSpecialProductIds] = useState<Set<string>>(new Set());
  const [loadingSpecialProducts, setLoadingSpecialProducts] = useState<boolean>(false);
  // Ref to prevent the specialId URL-sync effect from restoring a cleared specialId
  const clearingSpecialIdRef = useRef(false);

  const [selectedSort, setSelectedSort] = useState<string>('default');
  const [isSortCollapsed, setIsSortCollapsed] = useState<boolean>(true);
  const [dotPosition, setDotPosition] = useState<{ left: number; opacity: number }>({ left: 0, opacity: 0 });
  const [mobileDotPosition, setMobileDotPosition] = useState<{ left: number; opacity: number }>({ left: -30, opacity: 0 }); // Start hidden and far left
  const [showMobileDotAnimation, setShowMobileDotAnimation] = useState<boolean>(false);
  const [hasPlayedInitialAnimation, setHasPlayedInitialAnimation] = useState<boolean>(false);

  // Browser history state for drawer back button support
  const [historyPushed, setHistoryPushed] = useState<boolean>(false);

  // Track if drawer was manually closed to prevent reopening from deep link
  const drawerManuallyClosed = useRef<boolean>(false);
  // Track which productId was manually closed
  const lastClosedProductId = useRef<string | null>(null);
  // Track currently selected product ID to prevent deep link effect from overwriting programmatic selections
  const currentProductIdRef = useRef<string | null>(null);

  // Marquee state for text scrolling (both mobile and desktop)
  const [activeMarquees, setActiveMarquees] = useState<Set<string>>(new Set());
  const [stoppingMarquees, setStoppingMarquees] = useState<Set<string>>(new Set());
  const marqueeTimeouts = React.useRef<Map<string, NodeJS.Timeout>>(new Map());
  
  // Cleanup marquee timeouts on unmount
  useEffect(() => {
    return () => {
      marqueeTimeouts.current.forEach(timeout => clearTimeout(timeout));
      marqueeTimeouts.current.clear();
    };
  }, []);

  
  // Potency filter states
  const [isPotencyCollapsed, setIsPotencyCollapsed] = useState<boolean>(true);
  // Dynamic potency ranges based on actual product data
  const [potencyRanges, setPotencyRanges] = useState({
    thc: { min: 0, max: 40, step: 1 },
    cbd: { min: 0, max: 25, step: 1 }
  });
  // Removed tab state as we're showing both sliders at once
  const [thcRange, setThcRange] = useState<[number, number]>([0, 40]);
  const [cbdRange, setCbdRange] = useState<[number, number]>([0, 25]);
  
  // Track whether user has interacted with potency sliders
  const [hasInteractedWithThc, setHasInteractedWithThc] = useState<boolean>(false);
  const [hasInteractedWithCbd, setHasInteractedWithCbd] = useState<boolean>(false);
  
  // Edible potency filter states
  const [isEdiblePotencyCollapsed, setIsEdiblePotencyCollapsed] = useState<boolean>(true);
  const [selectedEdibleThcValues, setSelectedEdibleThcValues] = useState<string[]>([]);
  const [selectedEdibleCbdValues, setSelectedEdibleCbdValues] = useState<string[]>([]);
  const [selectedCannabinoidValues, setSelectedCannabinoidValues] = useState<{ [key: string]: string[] }>({});
  
  // Animation states for collapsing filters
  const [isWeightCollapsing, setIsWeightCollapsing] = useState<boolean>(false);
  const [isBrandCollapsing, setIsBrandCollapsing] = useState<boolean>(false);
  const [isLineageCollapsing, setIsLineageCollapsing] = useState<boolean>(false);
  const [isTerpeneCollapsing, setIsTerpeneCollapsing] = useState<boolean>(false);
  const [isCannabinoidCollapsing, setIsCannabinoidCollapsing] = useState<boolean>(false);
  const [isSubcategoryCollapsing, setIsSubcategoryCollapsing] = useState<boolean>(false);
  const [isPotencyCollapsing, setIsPotencyCollapsing] = useState<boolean>(false);
  const [isEdiblePotencyCollapsing, setIsEdiblePotencyCollapsing] = useState<boolean>(false);
  const [isSortCollapsing, setIsSortCollapsing] = useState<boolean>(false);
  
  // Debounced versions for filtering (300ms delay)
  const debouncedThcRange = useDebounce(thcRange, 300);
  const debouncedCbdRange = useDebounce(cbdRange, 300);

  // Helper function to handle filter collapse with animation
  const handleFilterToggle = (
    isCollapsed: boolean,
    setIsCollapsed: (value: boolean) => void,
    setIsCollapsing: (value: boolean) => void
  ) => {
    if (!isCollapsed) {
      // Start collapsing animation
      setIsCollapsing(true);
      // Wait for animation to complete, then hide content
      setTimeout(() => {
        setIsCollapsed(true);
        setIsCollapsing(false);
      }, 250); // Match the collapseAccordion animation duration
    } else {
      // Expand immediately
      setIsCollapsed(false);
    }
  };
  
  // Mobile filters sidebar state
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState<boolean>(false);

  // Mobile header redesign state (≤767px only)
  const [isMobileSearchExpanded, setIsMobileSearchExpanded] = useState<boolean>(false);
  const [isMobileSearchCollapsing, setIsMobileSearchCollapsing] = useState<boolean>(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState<boolean>(false);

  // Handle mobile search collapse with animation
  const handleMobileSearchClose = useCallback(() => {
    setIsMobileSearchCollapsing(true);
    setTimeout(() => {
      setIsMobileSearchExpanded(false);
      setIsMobileSearchCollapsing(false);
    }, 250); // Match animation duration
  }, []);

  // Mobile and tablet detection state
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 1000);
  const [isTablet, setIsTablet] = useState<boolean>(window.innerWidth >= 768 && window.innerWidth <= 1024);
  const [isTabletSearchExpanded, setIsTabletSearchExpanded] = useState<boolean>(false);

  // Server-configured home collections
  const [serverCollections, setServerCollections] = useState<{ id: string; name: string; productIds: string[]; shuffleProducts?: boolean }[] | null>(null);
  const [expandedCollectionId, setExpandedCollectionId] = useState<string | null>(null);

  // Deal detection for conditional spacing
  const { deals: availableDeals, loading: dealsLoading } = useDeals(legacyMenuId || null);

  // Determine if deals are actually being shown
  const hasActiveDeals = !dealsLoading && availableDeals && (
    (availableDeals.dealType === 'text' && availableDeals.content) ||
    (availableDeals.dealType === 'image' && availableDeals.imageUrls?.mobile && availableDeals.imageUrls?.desktop)
  );

  // Product performance tracking
  const [productViewTimes, setProductViewTimes] = useState<Map<string, number>>(new Map());
  const [categoryHistory, setCategoryHistory] = useState<string[]>([]);
  const [variantInteractions, setVariantInteractions] = useState<Map<string, number>>(new Map());

  // Block third-party herb.co scripts that cause appendChild errors
  React.useEffect(() => {
    const blockHerbScripts = () => {
      const herbScripts = document.querySelectorAll('script[src*="herb.co"]');
      herbScripts.forEach(script => {
        script.remove();
      });
    };

    // Block existing scripts
    blockHerbScripts();
    
    // Set up mutation observer to block dynamically added scripts
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            if (element.tagName === 'SCRIPT' && 
                element.getAttribute && 
                element.getAttribute('src') && 
                element.getAttribute('src')!.includes('herb.co')) {
              element.remove();
            }
          }
        });
      });
    });
    
    observer.observe(document.head, { childList: true, subtree: true });
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      observer.disconnect();
    };
  }, []);

  // Initialize attribution tracking
  useEffect(() => {
    // Initialize attribution tracking
    attributionService.initializeSessionTracking();
    
    // Set store context for geographic attribution
    if (menuId) {
      journeyService.setCurrentStore(menuId);
    }
  }, [attributionService, journeyService, menuId]);

  // Set retailer ID for cart operations when menuId changes
  useEffect(() => {
    if (menuId) {
      setRetailerId(menuId);
    }
  }, [menuId, setRetailerId]);

  // Fetch server-configured home collections
  useEffect(() => {
    if (!legacyMenuId) return;
    const storeInfo = MENU_ID_TO_STORE[legacyMenuId];
    if (!storeInfo) return;
    const storeName = storeInfo.store;
    const menuType = storeInfo.type === 'medical' ? 'med' : 'rec';

    getPublishedCollections(storeName, menuType)
      .then(data => setServerCollections(data))
      .catch(() => setServerCollections(null));
  }, [legacyMenuId]);

  // Fetch popup alert for this store
  useEffect(() => {
    if (!legacyMenuId) {
      setPopupAlert(null);
      setPopupAlertAcknowledged(false);
      return;
    }
    const fetchPopupAlert = async () => {
      const alert = await getPopupAlertForStore(legacyMenuId);
      if (alert) {
        const hashKey = `popupAlert_${legacyMenuId}_${simpleHash(alert.message)}`;
        const alreadyAcknowledged = sessionStorage.getItem(hashKey) === 'true';
        setPopupAlert(alert);
        setPopupAlertAcknowledged(alreadyAcknowledged);
      } else {
        setPopupAlert(null);
        setPopupAlertAcknowledged(false);
      }
    };
    fetchPopupAlert();
  }, [legacyMenuId]);

  // Enable drag scrolling for the entire category bar
  useEffect(() => {
    if (loading) return;

    const categoryContainer = document.querySelector('.category-buttons') as HTMLElement;
    if (!categoryContainer) return;

    let isDown = false;
    let startX: number;
    let scrollLeft: number;
    let isDragging = false;

    const handleStart = (clientX: number, e: Event) => {
      isDown = true;
      isDragging = false;
      categoryContainer.style.cursor = 'grabbing';
      categoryContainer.style.userSelect = 'none';
      startX = clientX - categoryContainer.offsetLeft;
      scrollLeft = categoryContainer.scrollLeft;
      // Don't prevent default on start - let native scrolling work
    };

    const handleEnd = (e: Event) => {
      if (!isDown) return;
      
      isDown = false;
      categoryContainer.style.cursor = 'grab';
      categoryContainer.style.userSelect = '';
      
      // Prevent button clicks only if we were actively dragging
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
        
        // Reset drag state after a brief delay
        setTimeout(() => {
          isDragging = false;
        }, 100);
      } else {
        // Reset immediately if no dragging occurred
        isDragging = false;
      }
    };

    const handleMove = (clientX: number, e: Event) => {
      if (!isDown) return;
      
      const x = clientX - categoryContainer.offsetLeft;
      const walk = (x - startX) * 2; // Adjust multiplier for sensitivity
      
      // Mark as dragging if moved more than threshold (mobile needs higher threshold)
      const dragThreshold = 'ontouchstart' in window ? 15 : 5; // Higher threshold on touch devices
      if (Math.abs(walk) > dragThreshold) {
        isDragging = true;
        // Only prevent default once we're actually dragging
        e.preventDefault();
      }
      
      // Only apply manual scroll if we're dragging
      if (isDragging) {
        categoryContainer.scrollLeft = scrollLeft - walk;
      }
    };

    // Mouse events
    const handleMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return; // Only left mouse button
      handleStart(e.pageX, e);
    };

    const handleMouseUp = (e: MouseEvent) => handleEnd(e);
    const handleMouseMove = (e: MouseEvent) => handleMove(e.pageX, e);
    const handleMouseLeave = () => {
      if (isDown) {
        isDown = false;
        categoryContainer.style.cursor = 'grab';
        categoryContainer.style.userSelect = '';
      }
    };

    // Touch events
    const handleTouchStart = (e: TouchEvent) => {
      handleStart(e.touches[0].clientX, e);
    };

    const handleTouchEnd = (e: TouchEvent) => handleEnd(e);
    const handleTouchMove = (e: TouchEvent) => {
      handleMove(e.touches[0].clientX, e);
    };

    // Set initial cursor
    categoryContainer.style.cursor = 'grab';

    // Add event listeners
    categoryContainer.addEventListener('mousedown', handleMouseDown);
    categoryContainer.addEventListener('mouseup', handleMouseUp);
    categoryContainer.addEventListener('mousemove', handleMouseMove);
    categoryContainer.addEventListener('mouseleave', handleMouseLeave);
    
    categoryContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
    categoryContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
    categoryContainer.addEventListener('touchmove', handleTouchMove, { passive: false }); // Need non-passive for preventDefault when dragging

    // Cleanup
    return () => {
      categoryContainer.removeEventListener('mousedown', handleMouseDown);
      categoryContainer.removeEventListener('mouseup', handleMouseUp);
      categoryContainer.removeEventListener('mousemove', handleMouseMove);
      categoryContainer.removeEventListener('mouseleave', handleMouseLeave);
      
      categoryContainer.removeEventListener('touchstart', handleTouchStart);
      categoryContainer.removeEventListener('touchend', handleTouchEnd);
      categoryContainer.removeEventListener('touchmove', handleTouchMove);
      
      // Reset styles
      categoryContainer.style.cursor = '';
      categoryContainer.style.userSelect = '';
    };
  }, [loading]);

  // Disable hover effects during scroll (fixes Chrome column width bug)
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      const productGrid = document.querySelector('.product-grid') as HTMLElement;
      if (productGrid) {
        productGrid.classList.add('scrolling');
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          productGrid.classList.remove('scrolling');
        }, 150);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);


  // Handle mobile detection on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1000);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile filters are open
  useEffect(() => {
    if (isMobileFiltersOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileFiltersOpen]);

  // Adding to cart state
  const [addingToCart, setAddingToCart] = useState<Set<string>>(new Set());

  // DEBUG MODE - Controlled by environment variable
  const debugMode = process.env.REACT_APP_PRODUCT_GRID_DEBUG === 'true';
  const [debugProduct, setDebugProduct] = useState<MenuProduct | null>(null);
  const [showDebugPopup, setShowDebugPopup] = useState<boolean>(false);

  // DEAL MODE - Show deals for products with rewards
  const [dealProduct, setDealProduct] = useState<MenuProduct | null>(null);
  const [showDealPopup, setShowDealPopup] = useState<boolean>(false);
  const [dealRewards, setDealRewards] = useState<any[]>([]);
  const [dealLoading, setDealLoading] = useState<boolean>(false);
  
  // Deal descriptions for hover tooltips (productId -> {title, description})
  const [dealDescriptions, setDealDescriptions] = useState<Record<string, { title: string; description: string }>>({});

  // Function to fetch deal descriptions for products with rewards
  const fetchDealDescriptions = useCallback(async (products: MenuProduct[]) => {
    const menuIdToRetailerMap: Record<string, string> = {
      '6158': '9ac9d5ad-5e4e-41a3-b37f-6da013b423ae', // Stamford Med
      '6159': '9ac9d5ad-5e4e-41a3-b37f-6da013b423ae', // Stamford Rec
      '5796': '2ca5693a-0ce8-421a-8e6b-f07c2b498a29', // West Hartford Med
      '5797': '2ca5693a-0ce8-421a-8e6b-f07c2b498a29', // West Hartford Rec
      '857': '60e98181-4255-48d4-a3dd-8dde829d0620',  // Olney Med
      '5440': '60e98181-4255-48d4-a3dd-8dde829d0620', // Olney Rec
      '4855': '8b4140a0-14d5-4472-81ef-773707aa1756', // Voorhees Med
      '5377': '3c85c2b4-2228-43e5-9ec0-df7a96a0a02a', // Voorhees Rec
      '4545': '7b0dfd1a-101e-4641-b34e-413097825c49', // Exeter Med
      '4996': '7b0dfd1a-101e-4641-b34e-413097825c49', // Exeter Rec
      '6145': 'dcaee184-ca72-4e9f-a5fa-9c24613a0882'  // Essex Junction
    };

    let retailerId: string;
    if (menuIdToRetailerMap[menuId || '']) {
      retailerId = menuIdToRetailerMap[menuId || ''];
    } else if (menuId && menuId.includes('-')) {
      retailerId = menuId;
    } else {
      retailerId = menuId || '';
    }

    if (!retailerId) return;

    // Collect all unique special reward IDs from products
    const allRewardIds = new Set<string>();
    products.forEach(product => {
      product.variants.forEach(variant => {
        if (variant.specialRewardIds && variant.specialRewardIds.length > 0) {
          variant.specialRewardIds.forEach(id => allRewardIds.add(id));
        }
      });
    });

    if (allRewardIds.size === 0) return;

    try {
      const API_BASE_URL = process.env.REACT_APP_BACKEND_URL!;
      const response = await fetch(`${API_BASE_URL}/api/deals/${retailerId}/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          specialRewardIds: Array.from(allRewardIds)
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Create a mapping of reward ID to description
          const rewardIdToDescription: Record<string, string> = {};
          result.data.forEach((deal: any) => {
            if (deal.id && deal.description) {
              rewardIdToDescription[deal.id] = deal.description;
            }
          });

          // Map products to their deal information (title + description)
          const productDescriptions: Record<string, { title: string; description: string }> = {};
          products.forEach(product => {
            product.variants.forEach(variant => {
              if (variant.specialRewardIds && variant.specialRewardIds.length > 0) {
                // Use the first reward for the product
                const firstRewardId = variant.specialRewardIds[0];
                const matchingDeal = result.data.find((deal: any) => deal.id === firstRewardId);
                if (matchingDeal) {
                  productDescriptions[product.id] = {
                    title: matchingDeal.name || 'Special Deal',
                    description: matchingDeal.description || ''
                  };
                }
              }
            });
          });

          setDealDescriptions(productDescriptions);
        }
      }
    } catch (error) {
      console.error('Error fetching deal descriptions:', error);
    }
  }, [menuId]);


  // PRICING TYPE - Medical vs Recreational (use prop or default to medical)
  const [pricingType, setPricingType] = useState<'MEDICAL' | 'RECREATIONAL'>(initialPricingType);

  // FILTER ANIMATION STATE - Track products for smooth transitions
  const [previousProductIds, setPreviousProductIds] = useState<Set<string>>(new Set());
  const [animatingProducts, setAnimatingProducts] = useState<Map<string, 'in' | 'out'>>(new Map());
  
  // COMPOUND ANIMATION STATE - Track individual terpene/cannabinoid data points
  const [previousSelectedTerpenes, setPreviousSelectedTerpenes] = useState<string[]>([]);
  const [previousSelectedCannabinoids, setPreviousSelectedCannabinoids] = useState<string[]>([]);
  const [animatingCompounds, setAnimatingCompounds] = useState<Map<string, 'in' | 'out'>>(new Map());

  // Marquee helper functions for mobile text scrolling
  const isMobileDevice = useCallback(() => window.innerWidth <= 767, []);
  
  const isTextTruncated = (element: HTMLElement): boolean => {
    // Check if text contains ellipsis or if scrollWidth exceeds clientWidth
    const text = element.textContent || '';
    return text.includes('...') || element.scrollWidth > element.clientWidth;
  };
  
  const startMarqueeForProduct = (productId: string) => {
    
    // Clear any existing timeouts for this product
    const brandKey = `brand-${productId}`;
    const strainKey = `strain-${productId}`;
    
    [brandKey, strainKey, `cleanup-${brandKey}`, `cleanup-${strainKey}`, `delay-${productId}`].forEach(key => {
      if (marqueeTimeouts.current.has(key)) {
        clearTimeout(marqueeTimeouts.current.get(key));
        marqueeTimeouts.current.delete(key);
      }
    });

    // Check which elements need marquee
    const brandElement = document.getElementById(brandKey);
    const strainElement = document.getElementById(strainKey);
    const elementsToAnimate: string[] = [];
    
    if (brandElement && isTextTruncated(brandElement)) {
      elementsToAnimate.push(brandKey);
    }
    
    if (strainElement && isTextTruncated(strainElement)) {
      elementsToAnimate.push(strainKey);
    }
    
    // Start all marquees simultaneously
    if (elementsToAnimate.length > 0) {
      setActiveMarquees(prev => {
        const newSet = new Set(prev);
        elementsToAnimate.forEach(key => newSet.add(key));
        return newSet;
      });
      
      setStoppingMarquees(prev => {
        const newSet = new Set(prev);
        elementsToAnimate.forEach(key => newSet.delete(key));
        return newSet;
      });
      
      // Auto-stop after 8 seconds with smooth transition
      const timeout = setTimeout(() => {
        stopMarqueeForProduct(productId);
      }, 8000);
      
      // Store timeout for both elements
      elementsToAnimate.forEach(key => {
        marqueeTimeouts.current.set(key, timeout);
      });
    }
  };
  
  const stopMarqueeForProduct = (productId: string) => {
    const brandKey = `brand-${productId}`;
    const strainKey = `strain-${productId}`;

    // Clear existing auto-stop timeouts
    [brandKey, strainKey].forEach(key => {
      if (marqueeTimeouts.current.has(key)) {
        clearTimeout(marqueeTimeouts.current.get(key));
        marqueeTimeouts.current.delete(key);
      }
    });

    // Capture current text-indent and freeze as inline style before removing animation
    [brandKey, strainKey].forEach(key => {
      if (activeMarquees.has(key)) {
        const el = document.getElementById(key);
        const marqueeContent = el?.querySelector('.marquee-content') as HTMLElement | null;
        if (marqueeContent) {
          const currentIndent = getComputedStyle(marqueeContent).textIndent;
          marqueeContent.style.textIndent = currentIndent;
        }
      }
    });

    // Move from active → stopping (swaps CSS classes, removes animation)
    setStoppingMarquees(prev => {
      const newSet = new Set(prev);
      if (activeMarquees.has(brandKey)) newSet.add(brandKey);
      if (activeMarquees.has(strainKey)) newSet.add(strainKey);
      return newSet;
    });

    setActiveMarquees(prev => {
      const newSet = new Set(prev);
      newSet.delete(brandKey);
      newSet.delete(strainKey);
      return newSet;
    });

    // Next frame: remove inline text-indent so CSS transition smoothly returns to 0
    requestAnimationFrame(() => {
      [brandKey, strainKey].forEach(key => {
        const el = document.getElementById(key);
        const marqueeContent = el?.querySelector('.marquee-content') as HTMLElement | null;
        if (marqueeContent) {
          marqueeContent.style.textIndent = '';
        }
      });
    });

    // Cleanup after transition completes
    [brandKey, strainKey].forEach(key => {
      const cleanupKey = `cleanup-${key}`;
      if (marqueeTimeouts.current.has(cleanupKey)) {
        clearTimeout(marqueeTimeouts.current.get(cleanupKey));
      }
      const cleanupTimeout = setTimeout(() => {
        marqueeTimeouts.current.delete(cleanupKey);
        setStoppingMarquees(prev => {
          const newSet = new Set(prev);
          newSet.delete(key);
          return newSet;
        });
        // Clear any residual inline styles
        const el = document.getElementById(key);
        const marqueeContent = el?.querySelector('.marquee-content') as HTMLElement | null;
        if (marqueeContent) {
          marqueeContent.style.textIndent = '';
        }
      }, 500);
      marqueeTimeouts.current.set(cleanupKey, cleanupTimeout);
    });
  };
  
  const handleProductTileClick = useCallback((product: MenuProduct) => {
    // Only start marquee on mobile click, desktop uses hover
    if (isMobileDevice()) {
      startMarqueeForProduct(product.id);
    }

    // Open product detail drawer
    setSelectedProduct(product);
    currentProductIdRef.current = product.id; // Track to prevent deep link overwrite
    setIsDrawerOpen(true);

    // Track view_item event for GA4/Surfside
    const firstVariant = product.variants?.[0];
    if (firstVariant) {
      const itemPrice = pricingType === 'MEDICAL' ? firstVariant.priceMed : firstVariant.priceRec;
      analyticsService.trackViewItem({
        currency: 'USD',
        value: itemPrice || 0,
        items: [{
          item_id: product.id,
          item_name: product.name,
          item_category: product.category || 'Cannabis',
          item_brand: product.brand?.name || 'Unknown Brand',
          item_price: itemPrice || 0,
          item_quantity: 1,
          item_variant: firstVariant.option || undefined,
          location_id: menuId || 'unknown'
        }]
      });
    }

    // Push history state for back button support (only if not already pushed)
    if (!historyPushed) {
      window.history.pushState({ drawerOpen: true }, '');
      setHistoryPushed(true);
    }
  }, [historyPushed, pricingType, menuId]);
  
  const handleDrawerClose = useCallback((fromPopStateOrEvent?: boolean | any) => {
    // Check if this is actually a boolean (from popstate) or an event object (from onClick)
    const fromPopState = typeof fromPopStateOrEvent === 'boolean' ? fromPopStateOrEvent : false;

    // Mark drawer as manually closed and save which productId was closed
    drawerManuallyClosed.current = true;
    lastClosedProductId.current = selectedProduct?.id || null;
    currentProductIdRef.current = null; // Clear current selection

    // Close the drawer UI
    setIsDrawerOpen(false);
    setSelectedProduct(null);

    // Handle URL and browser history based on how drawer was closed
    if (historyPushed && !fromPopState) {
      // Drawer closed normally (X, backdrop, etc.)
      // Going back in history will automatically restore the previous URL (without productId)
      setHistoryPushed(false);
      window.history.back();
    } else if (fromPopState) {
      // Drawer closed by back button - just reset history flag
      // URL already updated by browser's back action
      setHistoryPushed(false);
    } else {
      // No history was pushed (edge case) - manually remove productId from URL
      const params = new URLSearchParams(window.location.search);
      params.delete('productId');
      setSearchParams(params, { replace: true });
    }
  }, [historyPushed, setSearchParams, selectedProduct]);

  // Handle browser back button for drawer
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (isDrawerOpen && historyPushed) {
        // Back button pressed while drawer is open - close it
        handleDrawerClose(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDrawerOpen, historyPushed, handleDrawerClose]);

  const handleProductTileHover = useCallback((productId: string) => {
    if (!isMobileDevice()) {
      const delayKey = `delay-${productId}`;
      // Cancel any existing delay for this product
      if (marqueeTimeouts.current.has(delayKey)) {
        clearTimeout(marqueeTimeouts.current.get(delayKey));
      }
      // Start marquee after 300ms hover delay
      const timeout = setTimeout(() => {
        marqueeTimeouts.current.delete(delayKey);
        startMarqueeForProduct(productId);
      }, 300);
      marqueeTimeouts.current.set(delayKey, timeout);
    }
  }, []);
  
  const handleProductTileLeave = useCallback((productId: string) => {
    // Cancel any pending hover delay
    const delayKey = `delay-${productId}`;
    if (marqueeTimeouts.current.has(delayKey)) {
      clearTimeout(marqueeTimeouts.current.get(delayKey));
      marqueeTimeouts.current.delete(delayKey);
    }
    // Stop marquee on both desktop and mobile
    stopMarqueeForProduct(productId);
  }, []);

  // Handle Vermont increment operations - cycle to next variant (flower and multi-variant concentrates)
  const handleVermontIncrement = useCallback(async (product: MenuProduct, variant: any) => {
    const isVermontStore = menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC;
    const isFlowerProduct = product.category?.toLowerCase() === 'flower';
    const isConcentrateProduct = product.category?.toLowerCase() === 'concentrates';
    const hasMultipleVariants = product.variants && product.variants.length > 1;
    
    if (!isVermontStore || (!isFlowerProduct && !(isConcentrateProduct && hasMultipleVariants))) return;

    // Set loading state for this product
    const itemKey = `vermont-${product.id}`;
    setAddingToCart(prev => new Set(prev).add(itemKey));

    try {
      // Get current variant from cart, or use first variant if not in cart
      const currentVariant = getCurrentVariantFromCart(product, cart) || getSortedVariants(product)[0];
      const nextVariant = getNextVariant(product, currentVariant);
      
      if (!nextVariant) {
        console.error('No next variant available');
        return;
      }
      
      // If current variant is already the last one, don't do anything
      if (currentVariant && currentVariant.id === nextVariant.id) {
        return;
      }

      // Remove current cart item if it exists
      const cartItem = findCartItemForProduct(product, cart);
      if (cartItem) {
        await removeFromCart(cartItem.id);
      }

      // Add next variant with quantity 1
      await addToCart(product, nextVariant, 1);
    } catch (error) {
      console.error('Error updating cart with variant switching:', error);
    } finally {
      // Clear loading state
      setAddingToCart(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemKey);
        return newSet;
      });
    }
  }, [menuId, addToCart, removeFromCart, cart]);

  const handleVermontDecrement = useCallback(async (product: MenuProduct, variant: any) => {
    const isVermontStore = menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC;
    const isFlowerProduct = product.category?.toLowerCase() === 'flower';
    const isConcentrateProduct = product.category?.toLowerCase() === 'concentrates';
    const hasMultipleVariants = product.variants && product.variants.length > 1;
    
    if (!isVermontStore || (!isFlowerProduct && !(isConcentrateProduct && hasMultipleVariants))) return;

    // Set loading state for this product
    const itemKey = `vermont-${product.id}`;
    setAddingToCart(prev => new Set(prev).add(itemKey));

    try {
      // Get current variant from cart
      const currentVariant = getCurrentVariantFromCart(product, cart);
      
      if (!currentVariant) {
        console.error('No current variant found in cart');
        return;
      }
      
      // Find the cart item for this product
      const cartItem = findCartItemForProduct(product, cart);
      if (!cartItem) {
        console.error('Cart item not found for decrement');
        return;
      }

      // Get previous variant
      const previousVariant = getPreviousVariant(product, currentVariant);

      if (!previousVariant) {
        // Remove item completely from cart (will automatically return to ADD TO CART button)
        await removeFromCart(cartItem.id);
      } else {
        // Remove current item and add previous variant
        await removeFromCart(cartItem.id);
        await addToCart(product, previousVariant, 1);
      }
    } catch (error) {
      console.error('Error updating cart with variant switching:', error);
    } finally {
      // Clear loading state
      setAddingToCart(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemKey);
        return newSet;
      });
    }
  }, [menuId, addToCart, removeFromCart, cart]);

  // Handle adding item to cart
  const handleAddToCart = useCallback(async (product: MenuProduct, variant: any) => {
    // Trigger marquee for truncated text on mobile when add to cart is clicked
    if (isMobileDevice()) {
      startMarqueeForProduct(product.id);
    }

    // Check if this is Vermont store
    const isVermontStore = menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC;
    
    // Always use 1g variant for add to cart, regardless of which variant is displayed
    const oneGramVariant = product.variants.find(v => 
      v.option === '1g' || v.option === '1.0g'
    ) || variant; // Fallback to displayed variant if no 1g found

    // For Vermont store, increment mode is automatically determined by cart state

    const itemKey = `${product.id}-${oneGramVariant.id}`;

    if (addingToCart.has(itemKey) || cartLoading) {
      return; // Prevent double-clicking
    }

    setAddingToCart(prev => new Set(prev).add(itemKey));
    
    // Calculate cart value once to avoid scope issues
    const currentCartValue = cart?.priceSummary?.total ? cart.priceSummary.total / 100 : 0; // Convert from cents
    
    try {
      // Check if item is in stock before attempting to add to cart
      const isInStock = (oneGramVariant.inventoryLevel || 0) > 0;
      if (!isInStock) {
        // Track stockout cart impact for business intelligence
        // NOTE: stockout_cart_impact event is blocked from frontend - disabled for now
        // businessIntelligenceService.trackStockoutCartImpact(product.id, currentCartValue);
      }

      // Check for Dutchie quantity threshold restrictions (FLOWER products with quantity = 1)
      // TEMPORARILY DISABLED - Testing actual Dutchie API behavior for quantity=1 items
      // const isFlowerProduct = product.category === 'FLOWER';
      // const hasLowInventory = (oneGramVariant.quantity || 0) === 1;
      // if (isFlowerProduct && hasLowInventory) {
      //   console.warn('[CART] ❌ BLOCKED: FLOWER product with low inventory (Dutchie restriction)!', {
      //     productCategory: product.category,
      //     variantOption: oneGramVariant.option,
      //     inventoryLevel: oneGramVariant.quantity,
      //     reason: 'Dutchie enforces minimum quantity thresholds for FLOWER products'
      //   });
      //   setErrorMessage(`"${product.name}" (${oneGramVariant.option}) is currently out of stock`);
      //   setIsInventoryError(true);
      //   setShowErrorPopup(true);
      //   return;
      // }

      // Maryland recreational infused product limit (12g flower equivalent max)
      if (wouldExceedInfusedLimit(cart, products, product, oneGramVariant, 1)) {
        setErrorMessage(`Adding this product would exceed the ${INFUSED_LIMIT_GRAMS}g limit for infused products`);
        setIsInventoryError(false);
        setShowErrorPopup(true);
        return;
      }

      await addToCart(product, oneGramVariant, 1);

      // Track add to cart event with safe data extraction
      const getVariantPrice = (variant: any, pricingType: string): number => {
        const price = pricingType === 'MEDICAL' ? variant.priceMed : variant.priceRec;
        return typeof price === 'number' ? price : 0;
      };

      const getThcContent = (product: any): number => {
        if (!product.potencyThc?.range || !Array.isArray(product.potencyThc.range)) return 0;
        const highValue = product.potencyThc.range[product.potencyThc.range.length - 1];
        const numericValue = parseFloat(highValue);
        return !isNaN(numericValue) ? numericValue : 0;
      };

      const getCbdContent = (product: any): number => {
        if (!product.potencyCbd?.range || !Array.isArray(product.potencyCbd.range)) return 0;
        const highValue = product.potencyCbd.range[product.potencyCbd.range.length - 1];
        const numericValue = parseFloat(highValue);
        return !isNaN(numericValue) ? numericValue : 0;
      };

      const itemPrice = getVariantPrice(oneGramVariant, pricingType);
      
      // Track variant selection pattern (track what was added to cart, not displayed)
      const cartVariantKey = `${product.id}-${oneGramVariant.option}`;
      setVariantInteractions(prev => {
        const newMap = new Map(prev);
        const currentCount = newMap.get(cartVariantKey) || 0;
        newMap.set(cartVariantKey, currentCount + 1);
        return newMap;
      });
      
      // Track product engagement duration if available
      const productViewStart = productViewTimes.get(product.id);
      const engagementDuration = productViewStart ? Date.now() - productViewStart : 0;
      
      // Track journey interactions
      journeyService.trackProductView(product.id);
      journeyService.trackCartAction(itemPrice);
      
      // Update session value for attribution
      attributionService.updateSessionValue(itemPrice);
      const attributionData = attributionService.getAttributionData();
      const journeyData = journeyService.getJourneyData();
      
      analyticsService.trackAddToCart({
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        variant_id: variant.id,
        price: itemPrice,
        cart_total_items: (cartItemCount || 0) + 1,
        cart_total_value: currentCartValue + itemPrice,
        store_id: menuId || 'unknown',
        category: product.category,
        subcategory: product.subcategory || '',
        brand: product.brand?.name || '',
        thc_content: getThcContent(product),
        cbd_content: getCbdContent(product),
        // Attribution data
        ...attributionData.utm_attribution,
        session_id: attributionData.sessionId,
        search_attribution: attributionData.search_attribution,
        filter_attribution: attributionData.filter_attribution,
        session_value: attributionData.session_insights.value,
        is_first_visit: attributionData.firstVisit,
        // Product performance data
        product_engagement_duration: engagementDuration,
        variant_option: variant.option,
        variant_interaction_count: variantInteractions.get(cartVariantKey) || 1,
        category_browsing_history: categoryHistory,
        // Customer journey data
        customer_segment: journeyData.customer_segment,
        is_new_customer: journeyData.is_new_customer,
        session_number: journeyData.session_number,
        lifetime_value: journeyData.lifetime_value,
        lifetime_purchases: journeyData.lifetime_purchases,
        cart_actions_this_session: journeyData.current_session_interactions.cart_actions,
        preferred_stores: journeyData.preferred_stores
      });
      
      // Mark high-value user actions for session replay
      if (itemPrice >= 50) {
        frustrationService.markHighValueUserAction('add_high_value_item', itemPrice);
        frustrationService.markSessionReplayPoint('high_value_add_to_cart', {
          product_id: product.id,
          product_name: product.name,
          item_value: itemPrice,
          cart_value: currentCartValue + itemPrice,
          customer_segment: journeyData.customer_segment
        });
      }
    } catch (err) {
      // Track failed add-to-cart as frustration signal
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      frustrationService.markSessionReplayPoint('failed_add_to_cart', {
        product_id: product.id,
        product_name: product.name,
        variant_id: variant.id,
        error_message: errorMessage,
        cart_value: currentCartValue,
        pricing_type: pricingType
      });
    } finally {
      setAddingToCart(prev => {
        const next = new Set(prev);
        next.delete(itemKey);
        return next;
      });
    }
  }, [addToCart, cartLoading, addingToCart, pricingType, menuId, legacyMenuId, products, cart]);

  // Handle popup alert acknowledgment
  const handlePopupAlertAcknowledge = useCallback(() => {
    if (popupAlert && legacyMenuId) {
      const hashKey = `popupAlert_${legacyMenuId}_${simpleHash(popupAlert.message)}`;
      sessionStorage.setItem(hashKey, 'true');
      setPopupAlertAcknowledged(true);
    }
  }, [popupAlert, legacyMenuId]);

  // DEBUG MODE - Handle debug button click
  const handleDebugClick = useCallback((product: MenuProduct) => {
    setDebugProduct(product);
    setShowDebugPopup(true);
  }, []);

  // DEAL MODE - Handle deal button click
  const handleDealClick = useCallback(async (product: MenuProduct) => {
    setDealProduct(product);
    setShowDealPopup(true);
    
    // Track deal popup view
    analyticsService.trackEvent('view_promotion', {
      promotion_id: product.id,
      promotion_name: `Deals for ${product.name}`,
      product_id: product.id,
      product_name: product.name,
      item_category: product.category,
      item_brand: product.brand?.name || '',
      store_id: menuId || 'unknown'
    });
    
    // Collect all reward IDs from all variants
    const allRewardIds = product.variants
      .flatMap(variant => variant.specialRewardIds || [])
      .filter((id, index, array) => array.indexOf(id) === index); // Remove duplicates
    
    // Calculate discount percentage for tracking
    let maxDiscountPercent = 0;
    product.variants.forEach(variant => {
      const regularPrice = pricingType === 'MEDICAL' ? variant.priceMed : variant.priceRec;
      const specialPrice = pricingType === 'MEDICAL' ? variant.specialPriceMed : variant.specialPriceRec;
      if (regularPrice && specialPrice && specialPrice < regularPrice) {
        const discount = Math.round(((regularPrice - specialPrice) / regularPrice) * 100);
        maxDiscountPercent = Math.max(maxDiscountPercent, discount);
      }
    });
    
    // Track select_promotion for enhanced ecommerce
    const promotionId = `${product.id}-deal`;
    const promotionName = allRewardIds.length > 0 ? 'reward-deal' : 'discount-deal';
    
    analyticsService.trackEvent('select_promotion', {
      promotion_id: promotionId,
      promotion_name: promotionName,
      creative_name: 'deal_badge',
      creative_slot: 'product_overlay',
      location_id: selectedCategory,
      discount_percentage: maxDiscountPercent,
      reward_ids: allRewardIds,
      items: [{
        item_id: product.id,
        item_name: product.name,
        item_category: product.category,
        item_brand: product.brand?.name || '',
        price: pricingType === 'MEDICAL' ? product.variants[0]?.priceMed : product.variants[0]?.priceRec
      }],
      store_id: menuId || 'unknown'
    });
    
    if (allRewardIds.length > 0) {
      // Set loading state
      setDealLoading(true);
      setDealRewards([]);
      
      try {
        // Map menu ID to retailer ID for backend API
        const menuIdToRetailerMap: Record<string, string> = {
          '6158': '9ac9d5ad-5e4e-41a3-b37f-6da013b423ae', // Stamford Med
          '6159': '9ac9d5ad-5e4e-41a3-b37f-6da013b423ae', // Stamford Rec
          '5796': '2ca5693a-0ce8-421a-8e6b-f07c2b498a29', // West Hartford Med
          '5797': '2ca5693a-0ce8-421a-8e6b-f07c2b498a29', // West Hartford Rec
          '857': '60e98181-4255-48d4-a3dd-8dde829d0620',  // Olney Med
          '5440': '60e98181-4255-48d4-a3dd-8dde829d0620', // Olney Rec
          '4855': '8b4140a0-14d5-4472-81ef-773707aa1756', // Voorhees Med
          '5377': '3c85c2b4-2228-43e5-9ec0-df7a96a0a02a', // Voorhees Rec
          '4545': '7b0dfd1a-101e-4641-b34e-413097825c49', // Exeter Med
          '4996': '7b0dfd1a-101e-4641-b34e-413097825c49', // Exeter Rec
          '6145': 'dcaee184-ca72-4e9f-a5fa-9c24613a0882'  // Essex Junction
        };

        // Check if menuId is already a retailer ID or needs mapping
        let retailerId: string;
        if (menuIdToRetailerMap[menuId || '']) {
          // menuId is a menu ID, map it to retailer ID
          retailerId = menuIdToRetailerMap[menuId || ''];
        } else if (menuId && menuId.includes('-')) {
          // menuId looks like a retailer ID (contains dashes), use it directly
          retailerId = menuId;
        } else {
          // Unknown format, use as-is and let backend handle it
          retailerId = menuId || '';
        }
        
        if (!retailerId) {
          console.error('No retailer ID found for menu ID:', menuId);
          setDealRewards([]);
          return;
        }

        // Call backend API to resolve special reward IDs
        const API_BASE_URL = process.env.REACT_APP_BACKEND_URL!;
        const response = await fetch(`${API_BASE_URL}/api/deals/${retailerId}/resolve`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            specialRewardIds: allRewardIds
          })
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const result = await response.json();
        if (result.success && result.data) {
          // Transform backend API response to match expected format
          const transformedRewards = result.data.map((deal: any) => ({
            id: deal.id,
            name: deal.name,
            description: deal.description,
            type: deal.type,
            image: deal.image,
            menuDisplayConfiguration: {
              name: deal.name,
              description: deal.description,
              image: deal.image
            }
          }));
          setDealRewards(transformedRewards);
        } else {
          setDealRewards([]);
        }
      } catch (error) {
        console.error('Failed to resolve rewards for deal popup:', error);
        setDealRewards([]);
      } finally {
        setDealLoading(false);
      }
    } else {
      setDealRewards([]);
      setDealLoading(false);
    }
  }, [pricingType, menuId]);

  // Handle responsive behavior for deals display
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1000);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle cart errors with popup
  useEffect(() => {
    if (cartError) {
      // Don't override custom inventory error messages
      if (isInventoryError && showErrorPopup) {
        return;
      }

      // Check for Dutchie validation errors (quantity threshold issues)
      const errorString = typeof cartError === 'string' ? cartError : JSON.stringify(cartError);
      if (errorString.includes('Validation errors') || errorString.includes('UNPROCESSABLE_ENTITY')) {
        setErrorMessage('This item is currently out of stock');
        setIsInventoryError(true);
        if (Date.now() - dismissedErrorAt > 100) {
          setShowErrorPopup(true);
        }
        return;
      }

      // Check for Maryland infused product limit
      if (errorString.includes('MARYLAND_INFUSED_LIMIT_EXCEEDED')) {
        setErrorMessage('Adding this product would exceed limits for infused products');
        setIsInventoryError(true);
        if (Date.now() - dismissedErrorAt > 100) {
          setShowErrorPopup(true);
        }
        return;
      }

      // Extract useful error message
      let displayMessage = cartError;

      try {
        // Try to parse the error if it's a JSON string with nested error structure
        if (typeof cartError === 'string' && cartError.includes('"message":')) {
          const errorData = JSON.parse(cartError);

          if (errorData.message && typeof errorData.message === 'string') {
            // Extract the clean message before any colon and JSON
            const cleanMessage = errorData.message.split(':')[0].trim();
            displayMessage = cleanMessage;
          }
        }
      } catch (e) {
        // If parsing fails, use the original error message
      }

      setErrorMessage(displayMessage);
      // Only show popup if enough time has passed since last dismissal (100ms debounce)
      if (Date.now() - dismissedErrorAt > 100) {
        setShowErrorPopup(true);
      }
    }
  }, [cartError, dismissedErrorAt, isInventoryError, showErrorPopup]);

  // Get cart item count - number of distinct items in cart
  const cartItemCount = useMemo(() => {
    return cart?.items?.length || 0;
  }, [cart?.items]);

  // Format cart count - memoized for performance
  const formattedCartCount = useMemo(() => {
    return cartItemCount.toString();
  }, [cartItemCount]);

  useEffect(() => {
    // Store the pricing type in localStorage for cart creation
    localStorage.setItem('cartPricingType', pricingType);

    // Dispatch custom event to notify CartContext of pricing type change
    window.dispatchEvent(new CustomEvent('pricingTypeChanged', { 
      detail: { pricingType } 
    }));
    
    const loadProducts = async () => {
      const startTime = Date.now();
      setLoadingStartTime(startTime);
      
      // Show a subtle progress indicator if API takes longer than 3 seconds
      const progressTimeout = setTimeout(() => {
        setShowLoadingProgress(true);
      }, 3000);
      
      try {
        const response = await MenuService.getMenuProducts(menuId);

        // Start fetching deal descriptions immediately (don't await)
        fetchDealDescriptions(response.products).catch(() => {});

        // Calculate how long the API call took
        const apiCallDuration = Date.now() - startTime;

        // Clear the progress timeout since API completed
        clearTimeout(progressTimeout);
        
        // Filter products for recreational menu - remove edibles over 100mg THC
        let filteredProducts = response.products;
        const menuInfo = MENU_ID_TO_STORE[menuId];
        
        // Use legacy menu ID as fallback for more accurate menu type detection
        const legacyMenuInfo = legacyMenuId ? MENU_ID_TO_STORE[legacyMenuId] : null;

        // Determine if this is Olney recreational menu using multiple checks:
        // 1. Check the pricingType prop passed from MenuPage (most reliable for shared retailer IDs)
        // 2. Check legacy menu ID mapping (5440 is recreational, 857 is medical)
        // 3. Fall back to menu ID mapping if available
        const isOlneyStore = (menuInfo?.store === 'olney' || legacyMenuInfo?.store === 'olney') && 
                           (menuInfo?.state === 'maryland' || legacyMenuInfo?.state === 'maryland');
        
        // Use pricingType prop as primary source, with legacy menu ID as fallback
        const isRecreationalMenu = initialPricingType === 'RECREATIONAL' || 
                                 legacyMenuInfo?.type === 'recreational' || 
                                 (menuInfo?.type === 'recreational' && !legacyMenuInfo);
        
        const isOlneyRecreationalMenu = isOlneyStore && isRecreationalMenu;
        
        if (isOlneyRecreationalMenu) {
          filteredProducts = response.products.filter(product => {
            if (exceedsRecreationalThcLimit(product)) {
              return false;
            }
            return true;
          });
        }
        
        // Apply menuTypes filtering to ensure products match the current menu type (medical vs recreational)
        const menuTypeFilteredProducts = filteredProducts.filter(product => {
          // Skip products without menuTypes
          if (!product.menuTypes || !Array.isArray(product.menuTypes) || product.menuTypes.length === 0) {
            return false;
          }

          // Check if product is available for current pricing type
          return product.menuTypes.includes(pricingType);
        });
        
        // Set products immediately without preloading images
        setProducts(menuTypeFilteredProducts);
        // Also update context so Cart can access products
        setContextProducts(menuTypeFilteredProducts);

        // Track product inventory and pricing for business intelligence
        menuTypeFilteredProducts.forEach(product => {
          product.variants.forEach(variant => {
            // Track inventory status
            const isInStock = variant.inventoryLevel > 0;
            businessIntelligenceService.trackProductInventory(
              product.id, 
              product.name, 
              variant.inventoryLevel || 0, 
              isInStock
            );
            
            // Track pricing information
            const currentPrice = pricingType === 'MEDICAL' ? variant.priceMed : variant.priceRec;
            if (currentPrice > 0) {
              businessIntelligenceService.trackPriceChange(
                product.id, 
                product.name, 
                currentPrice
              );
            }
          });
        });
        
        // Show content immediately — no artificial delays
        setImagesLoaded(true);
        setShowSkeletonReveal(true);
        setShimmerFadeOut(true);
        setProductsJustLoaded(true);
        setLoading(false);
        setTimeout(() => {
          setShowCascadeAnimations(true);
          setShowCartAnimation(true);
          setTimeout(() => setProductsJustLoaded(false), 1000);
        }, 50);
        
      } catch (err) {
        console.error('Error loading products:', err);
        clearTimeout(progressTimeout);
        setError(err instanceof Error ? err.message : 'Failed to load products');
        // On error, don't wait - show error immediately
        setLoading(false);
      }
    };

    loadProducts();
  }, [pricingType]);

  // Handle product deep linking - open drawer when productId is in URL
  useEffect(() => {
    const productIdParam = searchParams.get('productId');

    // Reset flags when URL has no productId (drawer fully closed and URL cleaned)
    if (!productIdParam) {
      if (drawerManuallyClosed.current) {
        drawerManuallyClosed.current = false;
        lastClosedProductId.current = null;
      }
      return; // Nothing to do without a productId
    }

    // Reset manually closed flag when a DIFFERENT productId appears (new product clicked)
    // This allows cart items to open the drawer even if it was previously closed
    // But prevents reopening the same product that was just closed
    if (productIdParam !== lastClosedProductId.current) {
      drawerManuallyClosed.current = false;
      lastClosedProductId.current = null; // Clear the closed ID once we've moved to a new product
    }

    // Only process if we have a productId, products are loaded, and drawer wasn't manually closed
    // Don't check isDrawerOpen - we want to allow switching between products
    if (productIdParam && products.length > 0 && !drawerManuallyClosed.current) {
      // Skip if this product is already selected (prevents overwriting programmatic selections)
      if (productIdParam === currentProductIdRef.current) {
        return;
      }

      const product = products.find(p => p.id === productIdParam);

      if (product) {
        setSelectedProduct(product);
        currentProductIdRef.current = product.id; // Track the selection
        setIsDrawerOpen(true);

        // Track deep link product view
        analyticsService.trackEvent('deep_link_product_view', {
          product_id: productIdParam,
          product_name: product.name,
          store_id: menuId || 'unknown'
        });

        // Track view_item event for GA4/Surfside
        const firstVariant = product.variants?.[0];
        if (firstVariant) {
          const itemPrice = pricingType === 'MEDICAL' ? firstVariant.priceMed : firstVariant.priceRec;
          analyticsService.trackViewItem({
            currency: 'USD',
            value: itemPrice || 0,
            items: [{
              item_id: product.id,
              item_name: product.name,
              item_category: product.category || 'Cannabis',
              item_brand: product.brand?.name || 'Unknown Brand',
              item_price: itemPrice || 0,
              item_quantity: 1,
              item_variant: firstVariant.option || undefined,
              location_id: menuId || 'unknown'
            }]
          });
        }

        // Push history state for back button support
        if (!historyPushed) {
          window.history.pushState({ drawerOpen: true }, '');
          setHistoryPushed(true);
        }
      } else {
        // Remove invalid productId from URL
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('productId');
        setSearchParams(newParams, { replace: true });
      }
    }
  // Note: selectedProduct and isDrawerOpen intentionally excluded from deps
  // This effect should only react to URL changes and products loading, not internal state
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, searchParams, menuId, historyPushed, setSearchParams, pricingType]);

  // Handle specialId deep linking - fetch products for the specified deal
  useEffect(() => {
    // If we're in the middle of a programmatic clear, skip URL→state sync
    // to prevent the effect from restoring the old specialId before the URL updates
    if (clearingSpecialIdRef.current) {
      clearingSpecialIdRef.current = false;
      setSpecialProductIds(new Set());
      setLoadingSpecialProducts(false);
      return;
    }

    const specialIdParam = searchParams.get('specialId');

    // Update specialId state if URL param changed
    if (specialIdParam !== specialId) {
      setSpecialId(specialIdParam);

      // When entering a special/deal view, clear all filters so the deal products show unfiltered
      if (specialIdParam) {
        setSelectedCategory('home');
        setSelectedCategories([]);
        setSelectedBrands([]);
        setSelectedWeights([]);
        setSelectedSubcategories([]);
        setSelectedLineages([]);
        setSelectedTerpenes([]);
        setSelectedCannabinoids([]);
        setQuery('');
        setSelectedSort('default');
      }
    }

    // If no specialId, clear the special product filter
    if (!specialIdParam) {
      setSpecialProductIds(new Set());
      setLoadingSpecialProducts(false);
      return;
    }

    // Fetch products for this special/deal
    const fetchSpecialProducts = async () => {
      if (!legacyMenuId) {
        return;
      }

      setLoadingSpecialProducts(true);

      try {
        const specialProducts = await MenuService.getProductsForSpecial(legacyMenuId, specialIdParam);
        const productIds = new Set(specialProducts.map(p => p.id));
        setSpecialProductIds(productIds);

        // Track special deep link usage
        analyticsService.trackEvent('deep_link_special_view', {
          special_id: specialIdParam,
          product_count: productIds.size,
          store_id: menuId || 'unknown'
        });
      } catch (error) {
        console.error('[Deep Link] Error fetching special products:', error);
        // On error, clear the filter so all products show
        setSpecialProductIds(new Set());
      } finally {
        setLoadingSpecialProducts(false);
      }
    };

    fetchSpecialProducts();
  }, [searchParams, specialId, legacyMenuId, menuId]);

  // Sync URL parameters when filters change (deep linking)
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    // Preserve productId if drawer is open, or keep it if products haven't loaded yet
    if (isDrawerOpen && selectedProduct) {
      params.set('productId', selectedProduct.id);
    } else if (!isDrawerOpen && selectedProduct === null && products.length > 0) {
      // Only delete productId if drawer is closed AND products have loaded
      // (this prevents deleting it before the deep link effect has a chance to run)
      params.delete('productId');
    }
    // If products haven't loaded yet (products.length === 0), keep productId in URL

    // Only add non-default values to keep URL clean
    // Use 'categories' (plural) for multi-category deep links, 'category' (singular) for single
    if (selectedCategories.length > 1) {
      params.set('categories', selectedCategories.join(','));
      params.delete('category');
    } else if (selectedCategories.length === 1) {
      params.set('category', selectedCategories[0]);
      params.delete('categories');
    } else if (selectedCategory && selectedCategory !== 'home') {
      params.set('category', selectedCategory);
      params.delete('categories');
    } else {
      params.delete('category');
      params.delete('categories');
    }

    if (selectedBrands.length > 0) {
      params.set('brands', selectedBrands.map(b => encodeURIComponent(b)).join(','));
    } else {
      params.delete('brands');
    }

    if (selectedWeights.length > 0) {
      params.set('weights', selectedWeights.join(','));
    } else {
      params.delete('weights');
    }

    if (selectedLineages.length > 0) {
      params.set('lineages', selectedLineages.join(','));
    } else {
      params.delete('lineages');
    }

    if (selectedTerpenes.length > 0) {
      params.set('terpenes', selectedTerpenes.join(','));
    } else {
      params.delete('terpenes');
    }

    if (selectedCannabinoids.length > 0) {
      params.set('cannabinoids', selectedCannabinoids.join(','));
    } else {
      params.delete('cannabinoids');
    }

    if (selectedSubcategories.length > 0) {
      params.set('subcategories', selectedSubcategories.join(','));
    } else {
      params.delete('subcategories');
    }

    // Preserve specialId in URL if present
    if (specialId) {
      params.set('specialId', specialId);
    } else {
      params.delete('specialId');
    }

    // Update URL without creating new history entry (replace mode for cleaner UX)
    setSearchParams(params, { replace: true });

    // Track deep link usage (only when filters are active)
    const filterParams = new URLSearchParams(params);
    filterParams.delete('productId'); // Don't count productId as a filter
    if (filterParams.toString()) {
      analyticsService.trackEvent('deep_link_used', {
        filter_state: filterParams.toString(),
        category: selectedCategory,
        brand_count: selectedBrands.length,
        store_id: menuId || 'unknown'
      });
    }
  }, [selectedCategory, selectedCategories, selectedBrands, selectedWeights, selectedLineages, selectedTerpenes, selectedCannabinoids, selectedSubcategories, specialId, isDrawerOpen, selectedProduct, searchParams, setSearchParams, menuId]);

  // Memoize the search config to prevent recreating on every render
  const searchConfig = useMemo(() => ({
    keys: SEARCHABLE_KEYS,
    threshold: 0.3,
    includeScore: true,
    includeMatches: false, // We don't need highlighting for now
  }), []); // Empty deps array since SEARCHABLE_KEYS is a constant

  // Initialize fuzzy search for products with debouncing (300ms delay)
  const { 
    results: searchResults, 
    query, 
    debouncedQuery,
    setQuery
  } = useFuzzySearch(products, searchConfig); // Use the hook's default delay (currently 10000ms for testing)

  // Track search events when debounced query changes
  useEffect(() => {
    if (debouncedQuery.trim()) {
      // Track search for attribution and journey
      attributionService.trackSearchTerm(debouncedQuery);
      journeyService.trackSearch(debouncedQuery);
      
      // Get attribution and journey data for search event
      const attributionData = attributionService.getAttributionData();
      const journeyData = journeyService.getJourneyData();
      
      // Enhanced search tracking with search quality metrics
      const searchQuality = {
        has_results: searchResults.length > 0,
        search_length: debouncedQuery.trim().length,
        search_type: debouncedQuery.trim().length === 1 ? 'single_character' : 
                     debouncedQuery.trim().length <= 3 ? 'short_query' : 
                     debouncedQuery.trim().length <= 10 ? 'medium_query' : 'long_query',
        contains_numbers: /\d/.test(debouncedQuery),
        contains_special_chars: /[^a-zA-Z0-9\s]/.test(debouncedQuery),
        is_brand_search: searchResults.some(result => 
          result.item.brand?.name?.toLowerCase().includes(debouncedQuery.toLowerCase())),
        is_category_search: ['flower', 'edible', 'concentrate', 'pre-roll', 'vape'].some(cat => 
          debouncedQuery.toLowerCase().includes(cat)),
        search_refinement: journeyData.current_session_interactions.searches?.length > 1
      };
      
      analyticsService.trackSearch({
        search_term: debouncedQuery,
        results_count: searchResults.length,
        store_id: menuId || 'unknown',
        ...attributionData.utm_attribution,
        session_id: attributionData.sessionId,
        search_sequence: attributionData.search_attribution.length,
        previous_searches: attributionData.search_attribution.slice(-3), // Last 3 searches
        // Customer journey data
        ...journeyData,
        customer_segment: journeyData.customer_segment,
        session_number: journeyData.session_number,
        search_count_this_session: journeyData.current_session_interactions.searches?.length || 0,
        // Enhanced search quality metrics
        ...searchQuality
      });
      
      // Track enhanced GA4 search results for world-class search analytics
      analyticsService.trackViewSearchResults({
        search_term: debouncedQuery,
        search_results_count: searchResults.length,
        search_filters_applied: [
          ...selectedBrands.map(brand => `brand:${brand}`),
          ...selectedWeights.map(weight => `weight:${weight}`),
          ...selectedLineages.map(lineage => `lineage:${lineage}`),
          ...selectedTerpenes.map(terpene => `terpene:${terpene}`),
          ...selectedCannabinoids.map(cannabinoid => `cannabinoid:${cannabinoid}`)
        ].filter(filter => filter.includes(':')),
        search_sort_method: selectedSort,
        search_category: selectedCategory !== 'home' ? selectedCategory : undefined,
        search_refinements: journeyData.current_session_interactions.searches?.slice(-2) // Last 2 searches
      });
      
      // Track specific event for no results searches
      if (searchResults.length === 0) {
        analyticsService.trackEvent('search_no_results', {
          search_term: debouncedQuery,
          search_length: debouncedQuery.trim().length,
          search_type: searchQuality.search_type,
          store_id: menuId || 'unknown',
          customer_segment: journeyData.customer_segment,
          search_sequence: attributionData.search_attribution.length
        });
        
        // Mark as frustration signal for session replay
        frustrationService.markSessionReplayPoint('search_no_results', {
          search_term: debouncedQuery,
          search_count: attributionData.search_attribution.length,
          customer_segment: journeyData.customer_segment,
          frustration_score: frustrationService.getFrustrationScore()
        });
      }
    } else if (debouncedQuery === '' && query === '') {
      // Track empty search clearing
      const journeyData = journeyService.getJourneyData();
      analyticsService.trackEvent('search_cleared', {
        store_id: menuId || 'unknown',
        customer_segment: journeyData.customer_segment,
        previous_search_count: journeyData.current_session_interactions.searches?.length || 0
      });
    }
  }, [debouncedQuery, searchResults.length, menuId, attributionService, query]);


  // Check if any filters are active (excluding 'all' selections)
  const hasActiveFilters = useMemo(() => {
    const hasSelectedCannabinoidValues = Object.values(selectedCannabinoidValues).some(values => values.length > 0);
    
    return (
      selectedCategory !== 'home' ||
      selectedWeights.length > 0 ||
      selectedBrands.length > 0 ||
      selectedSubcategories.length > 0 ||
      selectedLineages.length > 0 ||
      selectedTerpenes.length > 0 ||
      selectedCannabinoids.length > 0 ||
      (hasInteractedWithThc && (debouncedThcRange[0] > potencyRanges.thc.min || debouncedThcRange[1] < potencyRanges.thc.max)) ||
      (hasInteractedWithCbd && (debouncedCbdRange[0] > potencyRanges.cbd.min || debouncedCbdRange[1] < potencyRanges.cbd.max)) ||
      selectedEdibleThcValues.length > 0 ||
      selectedEdibleCbdValues.length > 0 ||
      hasSelectedCannabinoidValues ||
      query.trim() !== '' ||
      !!specialId
    );
  }, [selectedCategory, selectedWeights, selectedBrands, selectedSubcategories, selectedLineages, selectedTerpenes, selectedCannabinoids, debouncedThcRange, debouncedCbdRange, potencyRanges, query, hasInteractedWithThc, hasInteractedWithCbd, selectedEdibleThcValues, selectedEdibleCbdValues, selectedCannabinoidValues, specialId]);

  // Get filtered products - first apply search, then apply other filters
  const searchFilteredProducts = useMemo(() => {
    if (!debouncedQuery.trim()) {
      return products;
    }
    return searchResults.map(result => result.item);
  }, [products, debouncedQuery, searchResults]);



  // Memoized utility function to get filtered products excluding one filter
  const getFilteredProductsExcept = useMemo(() => {
    return (excludeFilter: 'category' | 'weight' | 'brand' | 'subcategory' | 'lineage' | 'terpene' | 'potency' | 'edible-potency') => {
      
      // Normalize category function
      const normalizeCategory = (category: string): string => {
        const lower = category.toLowerCase().trim();

        // Normalize pre-roll variations to "pre-rolls"
        const preRollVariations = ['pre-roll', 'pre-rolls', 'preroll', 'prerolls', 'pre roll', 'pre rolls', 'pre_roll', 'pre_rolls'];
        if (preRollVariations.includes(lower)) {
          return 'pre-rolls';
        }

        return lower; // Return lowercase for case-insensitive comparison
      };
      
      return searchFilteredProducts.filter(product => {
        // menuTypes filter - ensure product is available for current menu type
        // This is the primary filter that should always be applied
        if (!product.menuTypes || !Array.isArray(product.menuTypes) || !product.menuTypes.includes(pricingType)) {
          return false;
        }
        
        // Debug: Check if this is a Sweetspot product
        const isSweetspot = product.brand && product.brand.name && (
          product.brand.name.toLowerCase().includes('sweetspot') || 
          product.brand.name.toLowerCase().includes('sweet spot')
        );
        
        // Category filter with normalization
        // Skip category filter when viewing an expanded collection (filtered by productIds instead)
        if (expandedCollectionId) {
          // No category filtering — collection products are filtered in the filteredProducts memo
        } else if (excludeFilter !== 'category' && selectedCategories.length > 0) {
          // Special case for "deals" category - filter to only products with deals
          // but continue checking other filters (subcategory, brand, etc.)
          if (selectedCategories.includes('deals')) {
            if (!hasDeals(product)) {
              return false;
            }
            // Continue to check other filters below
          } else {
            const normalizedProductCategory = normalizeCategory(product.category);
            const normalizedSelectedCategories = selectedCategories.map(c => normalizeCategory(c));
            if (!normalizedSelectedCategories.includes(normalizedProductCategory)) {
              return false;
            }
          }
        } else if (excludeFilter !== 'category' && selectedCategory !== 'home') {
          // Fallback to single category (for UI clicks)
          if (selectedCategory === 'deals') {
            if (!hasDeals(product)) {
              return false;
            }
            // Continue to check other filters below
          } else {
            const normalizedProductCategory = normalizeCategory(product.category);
            const normalizedSelectedCategory = normalizeCategory(selectedCategory);
            if (normalizedProductCategory !== normalizedSelectedCategory) {
              return false;
            }
          }
        }
        
        // Weight filter
        if (excludeFilter !== 'weight' && selectedWeights.length > 0) {
          // Skip weight filter for products that shouldn't display weight (like edibles)
          if (!shouldDisplayWeight(product)) {
            return true; // Don't filter out products that don't show weight
          }
          
          const hasMatchingVariant = product.variants.some(variant => 
            selectedWeights.includes(formatWeight(variant.option))
          );
          if (!hasMatchingVariant) {
            return false;
          }
        }
        
        // Brand filter (case-insensitive comparison)
        if (excludeFilter !== 'brand' && selectedBrands.length > 0) {
          const productBrand = sanitizeBrandName(product.brand?.name || '').toLowerCase();
          const hasMatchingBrand = selectedBrands.some(brand => brand.toLowerCase() === productBrand);

          if (!hasMatchingBrand) {
            return false;
          }
        }

        // Subcategory filter
        if (excludeFilter !== 'subcategory' && selectedSubcategories.length > 0 && !selectedSubcategories.includes(product.subcategory || '')) {
          return false;
        }

        // Lineage filter
        if (excludeFilter !== 'lineage' && selectedLineages.length > 0 && !selectedLineages.includes(product.strainType || '')) {
          return false;
        }
        
        // Terpene filter - check if product contains any of the selected terpenes
        // Exclude edibles from terpene filtering since they don't have terpene data
        if (excludeFilter !== 'terpene' && selectedTerpenes.length > 0) {
          // Filter out edibles when terpene filter is active
          if (product.category?.toLowerCase() === 'edibles') {
            return false;
          }
          if (!productContainsAnyTerpene(product, selectedTerpenes)) {
            return false;
          }
        }
        
        // Cannabinoid filter - check if product contains any of the selected cannabinoids
        // Exclude edibles from cannabinoid filtering since they don't have cannabinoid data
        if (excludeFilter !== 'cannabinoid' && selectedCannabinoids.length > 0) {
          // Filter out edibles when cannabinoid filter is active
          if (product.category?.toLowerCase() === 'edibles') {
            return false;
          }
          if (!productContainsAnyCannabinoid(product, selectedCannabinoids)) {
            return false;
          }
        }
        
        // THC and CBD potency filters - only apply if user has interacted with sliders
        if (excludeFilter !== 'potency') {
          // THC potency filter - only apply if user has moved the slider
          if (hasInteractedWithThc && (debouncedThcRange[0] > potencyRanges.thc.min || debouncedThcRange[1] < potencyRanges.thc.max)) {
            const thcValue = getPotencyValue(product, 'thc');
            if (thcValue < debouncedThcRange[0] || thcValue > debouncedThcRange[1]) {
              return false;
            }
          }
          
          // CBD potency filter - only apply if user has moved the slider
          if (hasInteractedWithCbd && (debouncedCbdRange[0] > potencyRanges.cbd.min || debouncedCbdRange[1] < potencyRanges.cbd.max)) {
            const cbdValue = getPotencyValue(product, 'cbd');
            if (cbdValue < debouncedCbdRange[0] || cbdValue > debouncedCbdRange[1]) {
              return false;
            }
          }
        }
        
        // Dose filters - apply to all products with mg-based potency
        if (excludeFilter !== 'edible-potency') {
          // THC dose filter - only apply to products with mg units
          if (selectedEdibleThcValues.length > 0) {
            const productThcValue = product.potencyThc?.formatted;
            const productThcUnit = product.potencyThc?.unit;
            if (productThcUnit === 'mg' && (!productThcValue || !selectedEdibleThcValues.includes(productThcValue))) {
              return false;
            }
          }
          
          // CBD dose filter - only apply to products with mg units
          if (selectedEdibleCbdValues.length > 0) {
            const productCbdValue = product.potencyCbd?.formatted;
            const productCbdUnit = product.potencyCbd?.unit;
            if (productCbdUnit === 'mg' && (!productCbdValue || !selectedEdibleCbdValues.includes(productCbdValue))) {
              return false;
            }
          }
          
          // Cannabinoid dose filters - only apply to edibles with cannabinoids array
          if (product.category?.toLowerCase() === 'edibles' && product.cannabinoids) {
            for (const [cannabinoidName, selectedValues] of Object.entries(selectedCannabinoidValues)) {
              if (selectedValues.length > 0) {
                const productCannabinoid = product.cannabinoids.find(c => 
                  c.name && getCannabinoidDisplayName(c.name) === cannabinoidName
                );
                
                if (!productCannabinoid || 
                    productCannabinoid.unitSymbol !== 'MILLIGRAMS' || 
                    isEffectivelyZero(productCannabinoid.value) ||
                    !selectedValues.includes(`${productCannabinoid.value}mg`)) {
                  return false;
                }
              }
            }
          }
        }
        
        return true;
      });
    };
  }, [searchFilteredProducts, selectedCategory, selectedCategories, expandedCollectionId, selectedWeights, selectedBrands, selectedSubcategories, selectedLineages, selectedTerpenes, selectedCannabinoids, debouncedThcRange, debouncedCbdRange, hasInteractedWithThc, hasInteractedWithCbd, potencyRanges, selectedEdibleThcValues, selectedEdibleCbdValues, selectedCannabinoidValues]);

  // Get all unique categories from products that match all other filters
  const categories = useMemo(() => {
    const filteredForCategories = getFilteredProductsExcept('category');
    
    const rawCategories = filteredForCategories
      .map(product => product.category);
    
    // Normalize category names - convert pre-roll variations to "pre-rolls"
    const normalizeCategory = (category: string): string => {
      const lower = category.toLowerCase().trim();

      // Normalize pre-roll variations to "pre-rolls"
      const preRollVariations = ['pre-roll', 'pre-rolls', 'preroll', 'prerolls', 'pre roll', 'pre rolls', 'pre_roll', 'pre_rolls'];
      if (preRollVariations.includes(lower)) {
        return 'pre-rolls';
      }

      return lower; // Return lowercase for case-insensitive comparison
    };
    
    const uniqueCategories = Array.from(new Set(
      rawCategories.map(category => normalizeCategory(category))
    ));

    // Define the specific category order and display mapping
    const categoryOrderAndDisplay = [
      { keys: ['flower', 'flowers'], display: 'FLOWER' },
      { keys: ['pre-rolls'], display: 'PRE-ROLL' }, // Only match normalized "pre-rolls"
      { keys: ['vaporizer', 'vaporizers', 'vape', 'vapes', 'cartridge', 'cartridges'], display: 'VAPE' },
      { keys: ['edible', 'edibles'], display: 'EDIBLE' },
      { keys: ['concentrate', 'concentrates'], display: 'CONCENTRATE' },
      { keys: ['topical', 'topicals'], display: 'TOPICAL' }, // Second to last
      { keys: ['accessory', 'accessories'], display: 'ACCESSORY' } // Last
    ];
    
    // Create sorted categories based on the specific order
    const sortedCategories = [];
    
    // First, add categories in the specified order if they exist in the data
    categoryOrderAndDisplay.forEach(({ keys }) => {
      const foundCategory = uniqueCategories.find(cat => 
        keys.some(key => key === cat.toLowerCase().trim())
      );
      if (foundCategory) {
        sortedCategories.push(foundCategory);
      }
    });
    
    // Then add any remaining categories alphabetically
    const remainingCategories = uniqueCategories
      .filter(cat => !categoryOrderAndDisplay.some(({ keys }) => 
        keys.some(key => key === cat.toLowerCase().trim())
      ))
      .sort();
    
    sortedCategories.push(...remainingCategories);
    
    return ['home', 'deals', ...sortedCategories];
  }, [getFilteredProductsExcept]);

  // Update dot position when selected category changes
  React.useEffect(() => {
    const updateDotPosition = () => {
      // Don't try to position dot while loading
      if (loading) {
        return;
      }

      if (!selectedCategory) {
        setDotPosition({ left: 0, opacity: 0 });
        return;
      }

      // Ensure category is still available (not filtered out) - use case-insensitive check
      const categoryExists = categories.some(cat => cat.toLowerCase() === selectedCategory.toLowerCase());
      if (!categoryExists) {
        setDotPosition({ left: 0, opacity: 0 });
        return;
      }

      // Find the active category button - try both original case and normalized
      let activeButton = document.querySelector(`.category-btn[data-original-category="${selectedCategory}"]`) as HTMLElement;

      // If not found, try to find by case-insensitive match
      if (!activeButton) {
        const allButtons = document.querySelectorAll('.category-btn');
        activeButton = Array.from(allButtons).find(btn => {
          const dataCat = btn.getAttribute('data-original-category');
          return dataCat && dataCat.toLowerCase() === selectedCategory.toLowerCase();
        }) as HTMLElement;
      }

      const categoryContainer = document.querySelector('.category-buttons') as HTMLElement;

      if (activeButton && categoryContainer) {
        const containerRect = categoryContainer.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();

        // Skip calculation if elements aren't properly sized yet
        if (containerRect.width === 0 || buttonRect.width === 0) {
          setTimeout(updateDotPosition, 100); // Retry after layout is ready
          return;
        }

        // Simple button center calculation (like mobile)
        const scrollLeft = categoryContainer.scrollLeft || 0;
        const buttonCenterX = buttonRect.left - containerRect.left + scrollLeft + (buttonRect.width / 2);

        setDotPosition({
          left: buttonCenterX,
          opacity: 1
        });
      } else {
        // Button not found - might not be rendered yet, retry
        setTimeout(updateDotPosition, 100);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready, then add delay
    // Wait longer if cascade animation is running (buttons are animating into position)
    let timeoutId: NodeJS.Timeout;
    const delay = showCascadeAnimations ? 500 : 100; // Wait for cascade animation to complete
    requestAnimationFrame(() => {
      timeoutId = setTimeout(updateDotPosition, delay);
    });

    // Update on window resize to recalculate positions
    window.addEventListener('resize', updateDotPosition);

    // Update dot position when category container is scrolled
    const categoryContainer = document.querySelector('.category-buttons') as HTMLElement;
    if (categoryContainer) {
      categoryContainer.addEventListener('scroll', updateDotPosition);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateDotPosition);
      if (categoryContainer) {
        categoryContainer.removeEventListener('scroll', updateDotPosition);
      }
    };
  }, [selectedCategory, categories, loading, products.length, showCascadeAnimations]); // Added showCascadeAnimations to recalculate after animation

  // Update mobile dot position when selected category changes
  React.useEffect(() => {
    const updateMobileDotPosition = () => {
      // Don't try to position dot while loading
      if (loading) {
        return;
      }

      if (!selectedCategory) {
        setMobileDotPosition({ left: 0, opacity: 0 });
        return;
      }

      // Only update mobile dot on mobile screens (align with CSS breakpoint)
      if (window.innerWidth > 768) {
        setMobileDotPosition({ left: 0, opacity: 0 });
        return;
      }

      // Find the active category button - try both original case and normalized
      let activeButton = document.querySelector(`.category-btn[data-original-category="${selectedCategory}"]`) as HTMLElement;

      // If not found, try to find by case-insensitive match
      if (!activeButton) {
        const allButtons = document.querySelectorAll('.category-btn');
        activeButton = Array.from(allButtons).find(btn => {
          const dataCat = btn.getAttribute('data-original-category');
          return dataCat && dataCat.toLowerCase() === selectedCategory.toLowerCase();
        }) as HTMLElement;
      }

      const categoryContainer = document.querySelector('.category-buttons') as HTMLElement;

      if (activeButton && categoryContainer) {
        const containerRect = categoryContainer.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();

        // Skip calculation if elements aren't properly sized yet
        if (containerRect.width === 0 || buttonRect.width === 0) {
          setTimeout(updateMobileDotPosition, 100); // Retry after layout is ready
          return;
        }

        // Simple approach: just center the dot under the button center
        // Account for scroll position by getting the button's actual position relative to the document
        const scrollLeft = categoryContainer.scrollLeft || 0;

        // Calculate button center relative to container's content area (not viewport)
        const buttonRelativeLeft = buttonRect.left - containerRect.left + scrollLeft;
        const buttonCenterX = buttonRelativeLeft + (buttonRect.width / 2) - 2; // Offset 2px to the left

        setMobileDotPosition({
          left: buttonCenterX,
          opacity: 1
        });
      } else {
        setTimeout(updateMobileDotPosition, 100);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready, then add delay
    let timeoutId: NodeJS.Timeout;
    requestAnimationFrame(() => {
      timeoutId = setTimeout(updateMobileDotPosition, 100); // Increased delay for initial render
    });

    // Update on window resize to recalculate positions
    window.addEventListener('resize', updateMobileDotPosition);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateMobileDotPosition);
    };
  }, [selectedCategory, categories, loading, products.length]); // Added products.length to trigger on load

  // Set initial dot position when loading completes
  React.useEffect(() => {
    if (loading || hasPlayedInitialAnimation) return;
    
    const setInitialDotPosition = () => {
      // Find the "ALL" button and calculate final position
      const allButton = document.querySelector(`.category-btn[data-original-category="all"]`) as HTMLElement;
      const categoryContainer = document.querySelector('.category-buttons') as HTMLElement;
      
      if (allButton && categoryContainer) {
        const containerRect = categoryContainer.getBoundingClientRect();
        const buttonRect = allButton.getBoundingClientRect();
        
        // Skip if elements aren't properly sized yet
        if (containerRect.width === 0 || buttonRect.width === 0) {
          setTimeout(setInitialDotPosition, 100);
          return;
        }
        
        if (window.innerWidth > 768) {
          // Desktop: Skip setInitialDotPosition, let updateDotPosition handle it
        } else {
          // Mobile: Set position and trigger animation
          const buttonRelativeLeft = buttonRect.left - containerRect.left;
          const finalPosition = buttonRelativeLeft + (buttonRect.width / 2) - 2;

          setMobileDotPosition({
            left: finalPosition,
            opacity: 1
          });

          setShowMobileDotAnimation(true);
        }
        
        setHasPlayedInitialAnimation(true);
      }
    };
    
    // Run after loading completes
    const timeoutId = setTimeout(setInitialDotPosition, 50);
    
    return () => clearTimeout(timeoutId);
  }, [loading]); // Trigger when loading state changes

  // Get unique weights from products that match all other filters
  const weights = useMemo(() => {
    const filteredForWeights = getFilteredProductsExcept('weight');
    return ['all', ...Array.from(new Set(
      filteredForWeights
        .filter(product => shouldDisplayWeight(product)) // Only include products that should show weight
        .flatMap(product => 
          product.variants
            .map(variant => variant.option)
            .filter(option => option && option.toLowerCase() !== 'n/a' && isValidWeight(option))
            .map(option => formatWeight(option)) // Format weights with leading zeros
        )
    )).sort()];
  }, [getFilteredProductsExcept]);

  // Get unique brands from products that match all other filters
  const brands = useMemo(() => {
    const filteredForBrands = getFilteredProductsExcept('brand');
    const allBrands = filteredForBrands
      .map(product => sanitizeBrandName(product.brand?.name || ''))
      .filter(brandName => brandName && brandName.trim() !== '' && brandName !== 'undefined' && brandName !== 'null'); // Filter out blank/empty/invalid brands
    const uniqueBrands = Array.from(new Set(allBrands)).sort();

    return ['all', ...uniqueBrands];
  }, [getFilteredProductsExcept]);

  // Get unique subcategories from products that match all other filters
  const subcategories = useMemo(() => {
    const filteredForSubcategories = getFilteredProductsExcept('subcategory');
    const allSubcategories = filteredForSubcategories
      .map(product => product.subcategory || '')
      .filter(subcategory => subcategory && subcategory.trim() !== '' && subcategory !== 'undefined' && subcategory !== 'null' && subcategory !== 'DEFAULT');
    const uniqueSubcategories = Array.from(new Set(allSubcategories)).sort();

    return uniqueSubcategories; // No 'all' option for subcategories - they're additive filters
  }, [getFilteredProductsExcept]);

  // Get unique lineages from products that match all other filters (excluding edibles)
  const lineages = useMemo(() => {
    const filteredForLineages = getFilteredProductsExcept('lineage');
    const invalidLineages = ['THC', 'NOT_APPLICABLE'];
    return ['all', ...Array.from(new Set(
      filteredForLineages
        .filter(product => product.category?.toLowerCase() !== 'edibles')
        .map(product => product.strainType)
        .filter(strainType => strainType && !invalidLineages.includes(strainType))
    )).sort()];
  }, [getFilteredProductsExcept]);

  // Get unique terpenes from ALL products (not filtered) to prevent disappearing options
  const terpenes = useMemo(() => {
    const allTerpenes = new Set<string>();
    
    // Only include products that have terpene data and exclude edibles
    const productsWithTerpenes = searchFilteredProducts.filter(product => 
      product.category?.toLowerCase() !== 'edibles' &&
      product.terpenes && product.terpenes.length > 0 && 
      product.terpenes.some(t => !isEffectivelyZero(t.value))
    );
    
    // Extract ALL significant terpenes from each product (not just top one)
    productsWithTerpenes.forEach(product => {
      product.terpenes.forEach(terpene => {
        if (terpene.name && !isEffectivelyZero(terpene.value)) {
          const displayName = getTerpeneDisplayName(terpene.name);
          if (displayName) {
            allTerpenes.add(displayName);
          }
        }
      });
    });
    
    return ['all', ...Array.from(allTerpenes).sort()];
  }, [searchFilteredProducts]);

  // Get unique cannabinoids from ALL products (not filtered) to prevent disappearing options
  const cannabinoids = useMemo(() => {
    const allCannabinoids = new Set<string>();
    
    // Only include products that have cannabinoid data and exclude edibles
    const productsWithCannabinoids = searchFilteredProducts.filter(product => 
      product.category?.toLowerCase() !== 'edibles' &&
      product.cannabinoids && product.cannabinoids.length > 0 && 
      product.cannabinoids.some(c => !isEffectivelyZero(c.value))
    );
    
    // Extract all cannabinoids from each product, filtering out THC-related ones
    // Use display names for consistency with filter matching
    productsWithCannabinoids.forEach(product => {
      product.cannabinoids.forEach(cannabinoid => {
        if (cannabinoid.name && !isEffectivelyZero(cannabinoid.value) && 
            !isTHCRelatedCannabinoid(cannabinoid.name)) {
          const displayName = getCannabinoidDisplayName(cannabinoid.name);
          if (displayName) {
            allCannabinoids.add(displayName);
          }
        }
      });
    });
    
    return ['all', ...Array.from(allCannabinoids).sort()];
  }, [searchFilteredProducts]);

  // Get unique mg-based potency values from products that match all other filters (excluding dose filter)
  const mgPotencyValues = useMemo(() => {
    const filteredForMgPotency = getFilteredProductsExcept('edible-potency');
    return getUniqueMgPotencyValues(filteredForMgPotency);
  }, [getFilteredProductsExcept]);

  // Check if there are any mg-based products to show the dose filter
  const shouldShowDoseFilter = useMemo(() => {
    const filteredProducts = getFilteredProductsExcept('edible-potency');
    return hasAnyMgBasedPotency(filteredProducts);
  }, [getFilteredProductsExcept]);

  // Reset dose filters when there are no mg-based products
  React.useEffect(() => {
    if (!shouldShowDoseFilter) {
      setSelectedEdibleThcValues([]);
      setSelectedEdibleCbdValues([]);
      setSelectedCannabinoidValues({});
    }
  }, [shouldShowDoseFilter]);

  // Reset weight filter if current selection is not available in the new category
  React.useEffect(() => {
    // Only validate after products have loaded (weights.length > 1 means we have more than just 'all')
    if (selectedWeights.length > 0 && weights.length > 1) {
      const validWeights = selectedWeights.filter(weight => weights.includes(weight));
      if (validWeights.length !== selectedWeights.length) {
        setSelectedWeights(validWeights);
      }
    }
  }, [selectedCategory, weights]); // Removed selectedWeights from deps to prevent infinite loop
  
  // Reset brand filter if current selection is not available in the new category/weight combination
  React.useEffect(() => {
    // Only validate brands after products have loaded (brands.length > 1 means more than just 'all')
    if (selectedBrands.length > 0 && brands.length > 1) {
      // Case-insensitive brand matching
      const validBrands = selectedBrands.filter(brand =>
        brands.some(availableBrand => availableBrand.toLowerCase() === brand.toLowerCase())
      );
      if (validBrands.length !== selectedBrands.length) {
        setSelectedBrands(validBrands);
      }
    }
  }, [selectedCategory, selectedWeights, brands]); // Removed selectedBrands from deps to prevent infinite loop
  
  // Reset lineage filter if current selection is not available in the new category/weight/brand combination
  React.useEffect(() => {
    if (selectedLineages.length > 0) {
      const validLineages = selectedLineages.filter(lineage => lineages.includes(lineage));
      if (validLineages.length !== selectedLineages.length) {
        setSelectedLineages(validLineages);
      }
    }
  }, [selectedCategory, selectedWeights, selectedBrands, lineages]); // Removed selectedLineages from deps to prevent infinite loop

  // Reset subcategory filter if current selection is not available in the new category
  React.useEffect(() => {
    if (loading) return; // Don't reset while products are still loading (prevents clearing URL-sourced params on mount)
    if (selectedSubcategories.length > 0 && subcategories.length > 0) {
      const validSubcategories = selectedSubcategories.filter(sub => subcategories.includes(sub));
      if (validSubcategories.length !== selectedSubcategories.length) {
        setSelectedSubcategories(validSubcategories);
      }
    } else if (selectedSubcategories.length > 0 && subcategories.length === 0) {
      // No valid subcategories for this category, clear all
      setSelectedSubcategories([]);
    }
  }, [loading, selectedCategory, selectedCategories, subcategories]); // Removed selectedSubcategories from deps to prevent infinite loop

  // Note: Removed automatic reset of terpene and cannabinoid filters to prevent disappearing options.
  // All available terpenes and cannabinoids are now always shown regardless of other active filters.



  // Reset sort when terpene/cannabinoid filters change if current sort is no longer valid
  useEffect(() => {
    if (selectedSort.startsWith('terpene-')) {
      // Extract terpene name and direction from format: terpene-{name}-{direction}
      const parts = selectedSort.split('-');
      const direction = parts[parts.length - 1]; // 'high' or 'low'
      const terpene = parts.slice(1, -1).join('-'); // Everything between 'terpene' and direction
      
      if (!selectedTerpenes.includes(terpene)) {
        setSelectedSort('default');
      }
    } else if (selectedSort.startsWith('cannabinoid-')) {
      // Extract cannabinoid name and direction from format: cannabinoid-{name}-{direction}
      const parts = selectedSort.split('-');
      const direction = parts[parts.length - 1]; // 'high' or 'low'
      const cannabinoid = parts.slice(1, -1).join('-'); // Everything between 'cannabinoid' and direction
      
      if (!selectedCannabinoids.includes(cannabinoid)) {
        setSelectedSort('default');
      }
    }
  }, [selectedTerpenes, selectedCannabinoids, selectedSort]);

  // Generate dynamic sort options based on selected filters
  const getDynamicSortOptions = () => {
    const baseOptions = [
      { value: 'price-high', label: 'PRICE: High to Low' },
      { value: 'price-low', label: 'PRICE: Low to High' }
    ];
    
    // Check if there are staff picks available in current filtered products
    const hasStaffPicks = filteredProducts.some(product => product.staffPick);
    
    // Check if there are deals available in current filtered products
    const hasDealsAvailable = filteredProducts.some(product => hasDeals(product));

    // Add DEALS FIRST option first if deals are available
    if (hasDealsAvailable) {
      baseOptions.unshift({ value: 'deals-first', label: 'DEALS FIRST' });
    }
    
    // Add FEATURED option after deals if staff picks are available
    if (hasStaffPicks) {
      baseOptions.unshift({ value: 'featured', label: 'FEATURED' });
    }
    
    // Only add THC sorting for categories where it makes sense
    const normalizedCategory = selectedCategory.toLowerCase();
    const isTopicalOrAccessory = normalizedCategory === 'topical' || normalizedCategory === 'accessory' || normalizedCategory === 'accessories' || normalizedCategory === 'apparel';

    if (!isTopicalOrAccessory) {
      baseOptions.push(
        { value: 'thc-high', label: 'THC: High to Low' },
        { value: 'thc-low', label: 'THC: Low to High' }
      );
    }
    
    // Add CBD sorting for edibles category (handle both 'edible' and 'edibles')
    if (normalizedCategory === 'edibles' || normalizedCategory === 'edible') {
      baseOptions.push(
        { value: 'cbd-high', label: 'CBD: High to Low' },
        { value: 'cbd-low', label: 'CBD: Low to High' }
      );
    }

    // Add total terpenes sorting for categories that display terpene data and have terpene data available
    const excludedCategories = ['edibles', 'edible', 'topical', 'accessory', 'accessories', 'apparel'];
    if (!excludedCategories.includes(normalizedCategory) && terpenes.length > 1) {
      baseOptions.push(
        { value: 'total-terpenes-high', label: 'TOTAL TERPENES: High to Low' },
        { value: 'total-terpenes-low', label: 'TOTAL TERPENES: Low to High' }
      );
    }

    const terpeneOptions = selectedTerpenes.flatMap(terpene => [
      { value: `terpene-${terpene}-high`, label: `${terpene.toUpperCase()}: High to Low` },
      { value: `terpene-${terpene}-low`, label: `${terpene.toUpperCase()}: Low to High` }
    ]);

    const cannabinoidOptions = selectedCannabinoids.flatMap(cannabinoid => [
      { value: `cannabinoid-${cannabinoid}-high`, label: `${cannabinoid.toUpperCase()}: High to Low` },
      { value: `cannabinoid-${cannabinoid}-low`, label: `${cannabinoid.toUpperCase()}: Low to High` }
    ]);

    return [...baseOptions, ...terpeneOptions, ...cannabinoidOptions];
  };

  // Apply sorting based on user selection
  const applySorting = useCallback((products: any[]) => {
    // Handle terpene sorting
    if (selectedSort.startsWith('terpene-')) {
      const parts = selectedSort.split('-');
      const direction = parts[parts.length - 1]; // 'high' or 'low'
      const terpene = parts.slice(1, -1).join('-'); // Everything between 'terpene' and direction
      
      return products
        .filter(product => {
          return product.terpenes?.some((t: any) => 
            t.name && !isEffectivelyZero(t.value) &&
            getTerpeneDisplayName(t.name) === terpene
          );
        })
        .sort((a, b) => {
          const aTerpene = a.terpenes?.find((t: any) => 
            t.name && getTerpeneDisplayName(t.name) === terpene
          );
          const bTerpene = b.terpenes?.find((t: any) => 
            t.name && getTerpeneDisplayName(t.name) === terpene
          );
          const aValue = aTerpene?.value || 0;
          const bValue = bTerpene?.value || 0;
          return direction === 'high' ? bValue - aValue : aValue - bValue;
        });
    }

    // Handle cannabinoid sorting
    if (selectedSort.startsWith('cannabinoid-')) {
      const parts = selectedSort.split('-');
      const direction = parts[parts.length - 1]; // 'high' or 'low'
      const cannabinoid = parts.slice(1, -1).join('-'); // Everything between 'cannabinoid' and direction
      return products
        .filter(product => {
          return product.cannabinoids?.some((c: any) => 
            c.name && !isEffectivelyZero(c.value) &&
            !isTHCRelatedCannabinoid(c.name) &&
            getCannabinoidDisplayName(c.name) === cannabinoid
          );
        })
        .sort((a, b) => {
          const aCannabinoid = a.cannabinoids?.find((c: any) => 
            c.name && getCannabinoidDisplayName(c.name) === cannabinoid
          );
          const bCannabinoid = b.cannabinoids?.find((c: any) => 
            c.name && getCannabinoidDisplayName(c.name) === cannabinoid
          );
          const aValue = aCannabinoid?.value || 0;
          const bValue = bCannabinoid?.value || 0;
          return direction === 'high' ? bValue - aValue : aValue - bValue;
        });
    }

    switch (selectedSort) {
      case 'featured':
        return products.sort((a, b) => {
          // Staff picks first, then regular products
          if (a.staffPick && !b.staffPick) return -1;
          if (!a.staffPick && b.staffPick) return 1;
          // If both are staff picks or both are regular, sort alphabetically by name
          return (a.name || '').localeCompare(b.name || '');
        });
      case 'deals-first':
        return products.sort((a, b) => {
          // Deals first (products with rewards or special pricing), then regular products
          const aHasDeal = hasDeals(a);
          const bHasDeal = hasDeals(b);
          if (aHasDeal && !bHasDeal) return -1;
          if (!aHasDeal && bHasDeal) return 1;
          // If both have deals or both are regular, sort alphabetically by name
          return (a.name || '').localeCompare(b.name || '');
        });
      case 'price-low':
        return products.sort((a, b) => {
          const aPrice = a.variants?.[0]?.priceMed || 0;
          const bPrice = b.variants?.[0]?.priceMed || 0;
          return aPrice - bPrice;
        });
      case 'price-high':
        return products.sort((a, b) => {
          const aPrice = a.variants?.[0]?.priceMed || 0;
          const bPrice = b.variants?.[0]?.priceMed || 0;
          return bPrice - aPrice;
        });
      case 'thc-high':
        return products
          .filter(product => {
            // Filter out products without THC data
            if (!product.potencyThc?.formatted) return false;
            
            // When in HOME category, exclude edibles, topical, and accessory from THC sorting to avoid confusion
            if (selectedCategory === 'home') {
              const category = product.category?.toLowerCase();
              const isEdible = category === 'edibles' || category === 'edible';
              const isTopical = category === 'topicals' || category === 'topical';
              const isAccessory = category === 'accessories' || category === 'accessory';
              if (isEdible || isTopical || isAccessory) return false;
            }

            // Also exclude topical and accessory when viewing those specific categories
            const currentCategory = selectedCategory?.toLowerCase();
            if (currentCategory === 'topical' || currentCategory === 'accessory') {
              return false; // Don't allow THC sorting for these categories
            }

            return true;
          })
          .sort((a, b) => {
            let aThc: number;
            let bThc: number;

            // Check if it's an edible (mg format)
            if (a.potencyThc?.unit === 'mg') {
              aThc = parseMgValue(a.potencyThc?.formatted);
            } else {
              aThc = parseFloat(a.potencyThc?.formatted || '0');
            }

            if (b.potencyThc?.unit === 'mg') {
              bThc = parseMgValue(b.potencyThc?.formatted);
            } else {
              bThc = parseFloat(b.potencyThc?.formatted || '0');
            }

            return bThc - aThc; // High to low: b - a
          });
      case 'thc-low':
        return products
          .filter(product => {
            // Filter out products without THC data
            if (!product.potencyThc?.formatted) return false;

            // When in HOME category, exclude edibles, topical, and accessory from THC sorting to avoid confusion
            if (selectedCategory === 'home') {
              const category = product.category?.toLowerCase();
              const isEdible = category === 'edibles' || category === 'edible';
              const isTopical = category === 'topicals' || category === 'topical';
              const isAccessory = category === 'accessories' || category === 'accessory';
              if (isEdible || isTopical || isAccessory) return false;
            }
            
            // Also exclude topical and accessory when viewing those specific categories
            const currentCategory = selectedCategory?.toLowerCase();
            if (currentCategory === 'topical' || currentCategory === 'accessory') {
              return false; // Don't allow THC sorting for these categories
            }
            
            return true;
          })
          .sort((a, b) => {
            let aThc: number;
            let bThc: number;
            
            // Check if it's an edible (mg format)
            if (a.potencyThc?.unit === 'mg') {
              aThc = parseMgValue(a.potencyThc?.formatted);
            } else {
              aThc = parseFloat(a.potencyThc?.formatted || '0');
            }
            
            if (b.potencyThc?.unit === 'mg') {
              bThc = parseMgValue(b.potencyThc?.formatted);
            } else {
              bThc = parseFloat(b.potencyThc?.formatted || '0');
            }
            
            return aThc - bThc; // Low to high: a - b
          });
      case 'cbd-high':
        return products
          .filter(product => product.potencyCbd?.formatted)
          .sort((a, b) => {
            let aCbd: number;
            let bCbd: number;
            
            // Check if it's an edible (mg format)
            if (a.potencyCbd?.unit === 'mg') {
              aCbd = parseMgValue(a.potencyCbd?.formatted);
            } else {
              aCbd = parseFloat(a.potencyCbd?.formatted || '0');
            }
            
            if (b.potencyCbd?.unit === 'mg') {
              bCbd = parseMgValue(b.potencyCbd?.formatted);
            } else {
              bCbd = parseFloat(b.potencyCbd?.formatted || '0');
            }
            
            return bCbd - aCbd; // High to low: b - a
          });
      case 'cbd-low':
        return products
          .filter(product => product.potencyCbd?.formatted)
          .sort((a, b) => {
            let aCbd: number;
            let bCbd: number;
            
            // Check if it's an edible (mg format)
            if (a.potencyCbd?.unit === 'mg') {
              aCbd = parseMgValue(a.potencyCbd?.formatted);
            } else {
              aCbd = parseFloat(a.potencyCbd?.formatted || '0');
            }
            
            if (b.potencyCbd?.unit === 'mg') {
              bCbd = parseMgValue(b.potencyCbd?.formatted);
            } else {
              bCbd = parseFloat(b.potencyCbd?.formatted || '0');
            }
            
            return aCbd - bCbd; // Low to high: a - b
          });
      case 'total-terpenes-high':
        return products
          .filter(product => hasValidTerpeneData(product))
          .sort((a, b) => {
            const aTotalTerpenes = getTotalTerpenePercentage(a);
            const bTotalTerpenes = getTotalTerpenePercentage(b);
            return bTotalTerpenes - aTotalTerpenes; // High to low: b - a
          });
      case 'total-terpenes-low':
        return products
          .filter(product => hasValidTerpeneData(product))
          .sort((a, b) => {
            const aTotalTerpenes = getTotalTerpenePercentage(a);
            const bTotalTerpenes = getTotalTerpenePercentage(b);
            return aTotalTerpenes - bTotalTerpenes; // Low to high: a - b
          });
      default:
        // Default sorting behavior (unchanged from original)
        if (selectedCategory === 'home') {
          const categoryOrder = [
            ['flower', 'flowers'],
            ['pre-rolls'], // Only the normalized form
            ['vaporizer', 'vaporizers', 'vape', 'vapes', 'cartridge', 'cartridges'],
            ['edible', 'edibles'],
            ['concentrate', 'concentrates'],
            ['topical', 'topicals'], // Second to last
            ['accessory', 'accessories'] // Last
          ];
          
          // Normalize category function for sorting
          const normalizeCategory = (category: string): string => {
            const lower = category.toLowerCase().trim();

            // Normalize pre-roll variations to "pre-rolls"
            const preRollVariations = ['pre-roll', 'pre-rolls', 'preroll', 'prerolls', 'pre roll', 'pre rolls', 'pre_roll', 'pre_rolls'];
            if (preRollVariations.includes(lower)) {
              return 'pre-rolls';
            }

            return lower; // Return lowercase for case-insensitive comparison
          };
          
          return products.sort((a, b) => {
            const aNormalized = normalizeCategory(a.category).toLowerCase().trim();
            const bNormalized = normalizeCategory(b.category).toLowerCase().trim();
            
            const aIndex = categoryOrder.findIndex(categoryGroup => 
              categoryGroup.some(cat => cat === aNormalized)
            );
            const bIndex = categoryOrder.findIndex(categoryGroup => 
              categoryGroup.some(cat => cat === bNormalized)
            );
            
            // If both are in preferred order, sort by that order
            if (aIndex !== -1 && bIndex !== -1) {
              return aIndex - bIndex;
            }
            // If only one is in preferred order, it comes first
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;
            // If neither is in preferred order, sort by category name then product name
            const categoryCompare = (a.category || '').localeCompare(b.category || '');
            return categoryCompare !== 0 ? categoryCompare : (a.name || '').localeCompare(b.name || '');
          });
        }
        
        // For specific category selection, sort by product name
        return products.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
  }, [selectedSort, selectedCategory]);

  // The final filtered products used for display
  const filteredProducts = useMemo(() => {
    let products = getFilteredProductsExcept('none' as any);

    // Apply multi-product filter (banner deep-linked to a curated set).
    // We preserve the URL order so the admin's ordering shows through.
    if (multiProductIds.length > 0) {
      const wanted = new Set(multiProductIds);
      const byId = new Map(products.map(p => [p.id, p]));
      products = multiProductIds
        .filter(id => wanted.has(id))
        .map(id => byId.get(id))
        .filter((p): p is typeof products[number] => Boolean(p));
      // Skip later sorting so the curated order survives.
      return products;
    }

    // Apply special/deal filter - only show products in the specified deal
    if (specialId && specialProductIds.size > 0) {
      products = products.filter(product => specialProductIds.has(product.id));
    }

    // Apply collection filter - show only products from the expanded collection
    if (expandedCollectionId && serverCollections) {
      const collection = serverCollections.find(sc => sc.id === expandedCollectionId);
      if (collection) {
        const collectionProductIds = new Set(collection.productIds);
        products = products.filter(product => collectionProductIds.has(product.id));
      }
    }

    // Apply sorting
    return applySorting(products);
  }, [getFilteredProductsExcept, selectedCategory, selectedSort, specialId, specialProductIds, expandedCollectionId, serverCollections, multiProductIds]);

  // Memoized cursor style to prevent object recreation on every render
  const productTileCursorStyle = useMemo(() => ({
    cursor: isMobileDevice() ? 'pointer' : 'default'
  }), [isMobileDevice]);

  // Track THC potency filter changes when debounced values change
  useEffect(() => {
    if (hasInteractedWithThc && (debouncedThcRange[0] !== potencyRanges.thc.min || debouncedThcRange[1] !== potencyRanges.thc.max)) {
      analyticsService.trackFilterApplied({
        filter_type: 'thc_potency',
        filter_value: `${debouncedThcRange[0]}%-${debouncedThcRange[1]}%`,
        results_count: filteredProducts.length,
        store_id: menuId || 'unknown'
      });
    }
  }, [debouncedThcRange, hasInteractedWithThc, potencyRanges.thc, menuId]); // Removed filteredProducts.length to prevent circular dependency

  // Track CBD potency filter changes when debounced values change
  useEffect(() => {
    if (hasInteractedWithCbd && (debouncedCbdRange[0] !== potencyRanges.cbd.min || debouncedCbdRange[1] !== potencyRanges.cbd.max)) {
      analyticsService.trackFilterApplied({
        filter_type: 'cbd_potency',
        filter_value: `${debouncedCbdRange[0]}%-${debouncedCbdRange[1]}%`,
        results_count: filteredProducts.length,
        store_id: menuId || 'unknown'
      });
    }
  }, [debouncedCbdRange, hasInteractedWithCbd, potencyRanges.cbd, menuId]); // Removed filteredProducts.length to prevent circular dependency

  // Create product cards for each variant, but filter by selected weights
  const productCards = useMemo(() => {
    const isVermontStore = menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC;
    
    return filteredProducts.flatMap(product => {
      if (isVermontStore) {
        // For Vermont: show one tile per product, use 1g variant or first available
        const oneGramVariant = product.variants.find(v => 
          v.option === '1g' || v.option === '1.0g'
        ) || product.variants[0];
        
        return [{
          id: `${product.id}-single`,
          product,
          variant: oneGramVariant
        }];
      } else {
        // For other stores: show separate tiles for each variant
        if (selectedWeights.length === 0) {
          // Show all variants when no specific weights are selected
          return product.variants.map(variant => ({
            id: `${product.id}-${variant.id}`,
            product,
            variant
          }));
        } else {
          // Only show variants that match the selected weights
          return product.variants
            .filter(variant => selectedWeights.includes(formatWeight(variant.option)))
            .map(variant => ({
              id: `${product.id}-${variant.id}`,
              product,
              variant
            }));
        }
      }
    });
  }, [filteredProducts, selectedWeights, menuId]);

  // Track scroll depth and time viewing
  useEffect(() => {
    let startTime = Date.now();
    let maxScrollDepth = 0;
    let scrollDepthThresholds = [25, 50, 75, 90];
    let trackedDepths = new Set();
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      // Add scrolling class to disable hover effects during scroll (fixes Chrome column width bug)
      const productGrid = document.querySelector('.product-grid') as HTMLElement;
      if (productGrid) {
        productGrid.classList.add('scrolling');
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          productGrid.classList.remove('scrolling');
        }, 150);
      }

      // Track scroll depth milestones
      scrollDepthThresholds.forEach(threshold => {
        if (scrollPercent >= threshold && !trackedDepths.has(threshold)) {
          trackedDepths.add(threshold);
          analyticsService.trackEvent('scroll_depth', {
            scroll_depth: threshold,
            category: selectedCategory,
            product_count: productCards.length,
            time_on_page: Date.now() - startTime,
            store_id: menuId || 'unknown'
          });
        }
      });

      maxScrollDepth = Math.max(maxScrollDepth, scrollPercent);
    };

    const handleBeforeUnload = () => {
      const timeOnPage = Date.now() - startTime;
      
      // Track time viewing if user spent more than 10 seconds
      if (timeOnPage > 10000) {
        analyticsService.trackEvent('time_on_category', {
          category: selectedCategory,
          time_viewing: timeOnPage,
          max_scroll_depth: maxScrollDepth,
          products_viewed: productCards.length,
          store_id: menuId || 'unknown'
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Also track when component unmounts (category change)
      const timeOnPage = Date.now() - startTime;
      if (timeOnPage > 5000) {
        analyticsService.trackEvent('category_viewing_time', {
          category: selectedCategory,
          time_viewing: timeOnPage,
          max_scroll_depth: maxScrollDepth,
          products_in_category: productCards.length,
          store_id: menuId || 'unknown'
        });
      }
    };
  }, [selectedCategory, productCards.length, menuId]);

  // Track product engagement completion when leaving category or component unmounts
  useEffect(() => {
    return () => {
      // Track engagement durations for all viewed products
      productViewTimes.forEach((startTime, productId) => {
        const engagementDuration = Date.now() - startTime;
        
        // Only track if engagement was meaningful (> 3 seconds)
        if (engagementDuration > 3000) {
          const product = productCards.find(card => card.product.id === productId)?.product;
          
          if (product) {
            analyticsService.trackEvent('product_engagement', {
              product_id: productId,
              product_name: product.name,
              product_category: product.category,
              product_brand: product.brand?.name || '',
              engagement_duration: engagementDuration,
              category_context: selectedCategory,
              store_id: menuId || 'unknown'
            });
          }
        }
      });
    };
  }, [productViewTimes, productCards, selectedCategory, menuId]);

  // Track product changes for smooth filter animations
  useEffect(() => {
    const currentProductIds = new Set(productCards.map(card => card.id));
    const newProducts = new Set([...currentProductIds].filter(id => !previousProductIds.has(id)));
    const removedProducts = new Set([...previousProductIds].filter(id => !currentProductIds.has(id)));
    
    if (newProducts.size > 0 || removedProducts.size > 0) {
      const newAnimatingProducts = new Map(animatingProducts);
      
      // Mark new products as filtering in
      newProducts.forEach(id => {
        newAnimatingProducts.set(id, 'in');
      });
      
      // Mark removed products as filtering out (they'll be removed after animation)
      removedProducts.forEach(id => {
        newAnimatingProducts.set(id, 'out');
      });
      
      setAnimatingProducts(newAnimatingProducts);
      
      // Clean up animation states after animation completes
      setTimeout(() => {
        setAnimatingProducts(new Map());
        setPreviousProductIds(currentProductIds);
      }, 1100); // Match the longest animation duration (0.6s + 0.5s stagger)
    } else if (previousProductIds.size === 0) {
      // Initial load - just set the current products without animation
      setPreviousProductIds(currentProductIds);
    }
  }, [productCards]);

  // Track view_item_list when products are displayed
  useEffect(() => {
    if (productCards.length > 0 && !loading) {
      // Prepare items for view_item_list event
      const items = productCards.slice(0, 10).map((card, index) => ({
        item_id: card.product.id,
        item_name: card.product.name,
        item_category: card.product.category,
        item_brand: card.product.brand?.name || '',
        price: pricingType === 'MEDICAL' ? card.variant.priceMed : card.variant.priceRec,
        item_list_name: selectedCategory === 'home' ? 'All Products' : selectedCategory,
        item_list_id: selectedCategory,
        index: index
      }));

      // Enhanced GA4 view_item_list with positioning and context
      const listType = debouncedQuery.trim() ? 'search' :
                       selectedCategory === 'home' ? 'category' :
                       'category';

      analyticsService.trackViewItemList({
        item_list_name: debouncedQuery.trim() ? `Search: ${debouncedQuery}` :
                        selectedCategory === 'home' ? 'All Products' : selectedCategory,
        item_list_id: debouncedQuery.trim() ? `search_${debouncedQuery.replace(/\s+/g, '_')}` : selectedCategory,
        items: items,
        list_type: listType,
        promotion_id: undefined, // Could be enhanced for deals pages
        promotion_name: undefined
      });
      
      // Legacy view_item_list for backward compatibility
      analyticsService.trackEvent('view_item_list', {
        item_list_name: selectedCategory === 'home' ? 'All Products' : selectedCategory,
        item_list_id: selectedCategory,
        items: items,
        number_of_items: productCards.length,
        store_id: menuId || 'unknown'
      });
    }
  }, [productCards, selectedCategory, loading, pricingType, menuId]);

  // Track compound filter changes for smooth animations
  useEffect(() => {
    const newTerpenes = selectedTerpenes.filter(t => !previousSelectedTerpenes.includes(t));
    const removedTerpenes = previousSelectedTerpenes.filter(t => !selectedTerpenes.includes(t));
    const newCannabinoids = selectedCannabinoids.filter(c => !previousSelectedCannabinoids.includes(c));
    const removedCannabinoids = previousSelectedCannabinoids.filter(c => !selectedCannabinoids.includes(c));
    
    if (newTerpenes.length > 0 || removedTerpenes.length > 0 || newCannabinoids.length > 0 || removedCannabinoids.length > 0) {
      const newAnimatingCompounds = new Map(animatingCompounds);
      
      // Mark new compounds as animating in
      newTerpenes.forEach(terpene => {
        newAnimatingCompounds.set(`terpene-${terpene}`, 'in');
      });
      newCannabinoids.forEach(cannabinoid => {
        newAnimatingCompounds.set(`cannabinoid-${cannabinoid}`, 'in');
      });
      
      // Mark removed compounds as animating out
      removedTerpenes.forEach(terpene => {
        newAnimatingCompounds.set(`terpene-${terpene}`, 'out');
      });
      removedCannabinoids.forEach(cannabinoid => {
        newAnimatingCompounds.set(`cannabinoid-${cannabinoid}`, 'out');
      });
      
      setAnimatingCompounds(newAnimatingCompounds);
      
      // Clean up animation states and update previous selections
      setTimeout(() => {
        setAnimatingCompounds(new Map());
        setPreviousSelectedTerpenes(selectedTerpenes);
        setPreviousSelectedCannabinoids(selectedCannabinoids);
      }, 400); // Slightly shorter than product animations
    } else if (previousSelectedTerpenes.length === 0 && previousSelectedCannabinoids.length === 0) {
      // Initial load
      setPreviousSelectedTerpenes(selectedTerpenes);
      setPreviousSelectedCannabinoids(selectedCannabinoids);
    }
  }, [selectedTerpenes, selectedCannabinoids]);

  if (loading) return (
    <>
      {/* Deal banner placeholder - OUTSIDE container on mobile to match loaded state */}
      <div className={`deals-banner-placeholder ${showCascadeAnimations ? 'cascade-in' : ''}`}>
        <div className="deals-skeleton-shimmer"></div>
      </div>
    </>
  );

  if (error) return (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <div style={{ color: '#FF0000' }}>Error: {error}</div>
    </div>
  );

  return (
    <>
      {/* Popup Alert Modal - blocks interaction until acknowledged */}
      {popupAlert && !popupAlertAcknowledged && (
        <PopupAlertModal
          message={popupAlert.message}
          onAcknowledge={handlePopupAlertAcknowledge}
        />
      )}

      {/* Deals Display - Always outside container; CSS handles desktop vs mobile layout */}
      {legacyMenuId && (
        <DealsDisplay menuId={legacyMenuId} menuType={pricingType === 'MEDICAL' ? 'medical' : 'recreational'} />
      )}

      {/* Conditional spacer when no deals are present */}
      {!hasActiveDeals && (
        <div className="no-deals-spacer"></div>
      )}

      <div className={`dutchie-menu-container ${imagesLoaded ? 'images-ready' : ''}`}>
        
        {/* Category Filters - Top Level */}
        <div className="category-filters">
        <div className="category-buttons">
          {categories.map((category, index) => (
            <button
              key={category}
              onClick={() => {
                // Track filter application
                analyticsService.trackFilterApplied({
                  filter_type: 'category',
                  filter_value: category,
                  results_count: getFilteredProductsExcept('category').length,
                  store_id: menuId || 'unknown'
                });

                // Track journey and cross-category browsing behavior
                journeyService.trackCategoryView(category);

                setCategoryHistory(prev => {
                  const newHistory = [...prev, category].slice(-5); // Keep last 5 categories

                  // Track category switching pattern
                  if (prev.length > 0 && prev[prev.length - 1] !== category) {
                    const journeyData = journeyService.getJourneyData();

                    analyticsService.trackEvent('category_switch', {
                      from_category: prev[prev.length - 1],
                      to_category: category,
                      category_sequence: newHistory,
                      switch_count: newHistory.length,
                      store_id: menuId || 'unknown',
                      // Customer journey context
                      customer_segment: journeyData.customer_segment,
                      is_new_customer: journeyData.is_new_customer,
                      preferred_categories: journeyData.preferred_categories
                    });
                  }

                  return newHistory;
                });

                setSelectedCategory(category);
                setSelectedCategories(category === 'home' ? [] : [category]);
                setExpandedCollectionId(null);
                setSelectedSort('default');

                // Scroll to products on mobile/tablet when category changes
                if (window.innerWidth <= 1286) {
                  setTimeout(() => {
                    const productsContainer = document.querySelector('.products-container') as HTMLElement;
                    if (productsContainer) {
                      if (window.innerWidth <= 767) {
                        // Mobile: mobile-header-redesign is the sticky header
                        const mobileHeader = document.querySelector('.mobile-header-redesign') as HTMLElement;
                        const offset = mobileHeader?.offsetHeight || 120;
                        const targetPosition = productsContainer.offsetTop - offset;
                        window.scrollTo({ top: targetPosition, behavior: 'smooth' });
                      } else {
                        // Tablet: category-filters is the sticky header
                        const categoryBar = document.querySelector('.category-filters') as HTMLElement;
                        const offset = categoryBar?.offsetHeight || 80;
                        const targetPosition = productsContainer.offsetTop - offset;
                        window.scrollTo({ top: targetPosition, behavior: 'smooth' });
                      }
                    }
                  }, 100);
                }
              }}
              className={`category-btn ${selectedCategory === category ? 'active' : ''} ${showCascadeAnimations ? 'cascade-in' : ''}`}
              data-original-category={category}
              style={{
                transitionDelay: showCascadeAnimations ? `${0.05 + index * 0.05}s` : '0s'
              }}
            >
              {getCategoryDisplayName(category)}
            </button>
          ))}
          {/* Desktop animated dot indicator - hidden until cascade completes */}
          <div
            className="category-dot-indicator desktop-dot"
            style={{
              left: `${dotPosition.left}px`,
              opacity: showCascadeAnimations ? dotPosition.opacity : 0
            }}
          />
          
          {/* Mobile simple dot indicator */}
          <div 
            className={`mobile-category-dot ${showMobileDotAnimation ? 'shoot-in' : ''}`}
            style={{
              left: `${mobileDotPosition.left}px`,
              opacity: mobileDotPosition.opacity
            }}
          >
            •
          </div>
        </div>
        
        {/* Search Input - Desktop */}
        <div className={`desktop-header-search-container ${showCascadeAnimations ? 'cascade-in' : ''}`}>
          <input
            type="text"
            placeholder="SEARCH"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="desktop-header-search-input"
          />
          <img
            src={magnifyingGlass}
            alt="Search"
            className="desktop-header-search-icon"
          />
        </div>

        {/* Cart Button */}
        <button
          className="menu-cart-toggle-btn"
          onClick={toggleCart}
          aria-label="Open cart"
        >
          <span className={`cart-text ${showCartAnimation ? 'cart-fade-in' : ''}`}>
            CART ({formattedCartCount})
          </span>
        </button>

        {/* Tablet Capsule Buttons (768px-1024px only - visibility controlled by CSS) */}
        {!isTabletSearchExpanded ? (
          <>
            <button
              className="tablet-search-capsule"
              onClick={() => setIsTabletSearchExpanded(true)}
            >
              <img src={magnifyingGlass} alt="" className="tablet-search-icon" />
              <span>SEARCH</span>
            </button>
            <button
              className="tablet-cart-capsule"
              onClick={toggleCart}
              aria-label="Open cart"
            >
              CART ({formattedCartCount})
            </button>
          </>
        ) : (
          <div className="tablet-search-expanded">
            <img src={magnifyingGlass} alt="" className="tablet-search-icon" />
            <input
              type="text"
              placeholder={`SEARCH ${selectedCategory === 'home' ? 'PRODUCTS' : getCategoryDisplayName(selectedCategory)}...`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              className="tablet-search-expanded-input"
            />
            <button
              className="tablet-search-close"
              onClick={() => setIsTabletSearchExpanded(false)}
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {/* Mobile Header Redesign (≤767px only - visibility controlled by CSS) */}
      <div className="mobile-header-redesign">
          {/* Row 1: Search + Cart capsules */}
          <div className="mobile-header-row-1">
            {!isMobileSearchExpanded ? (
              <>
                <button
                  className={`mobile-search-capsule ${showCascadeAnimations ? 'cascade-in' : ''}`}
                  onClick={() => setIsMobileSearchExpanded(true)}
                >
                  <img src={magnifyingGlass} alt="" className="mobile-search-icon" />
                  <span>SEARCH</span>
                </button>
                <button
                  className={`mobile-cart-capsule ${showCascadeAnimations ? 'cascade-in' : ''}`}
                  onClick={toggleCart}
                >
                  CART ({formattedCartCount})
                </button>
              </>
            ) : (
              <div className={`mobile-search-expanded ${isMobileSearchCollapsing ? 'collapsing' : ''}`}>
                <img src={magnifyingGlass} alt="" className="mobile-search-icon" />
                <input
                  type="text"
                  placeholder={`SEARCH ${selectedCategory === 'home' ? 'PRODUCTS' : getCategoryDisplayName(selectedCategory)}...`}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                  className="mobile-search-expanded-input"
                />
                <button
                  className="mobile-search-close"
                  onClick={handleMobileSearchClose}
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          <div className={`mobile-header-divider ${showCascadeAnimations ? 'cascade-in-1' : ''}`} />

          {/* Row 2: Categories + Sort dropdowns */}
          <div className="mobile-header-row-2">
            <div className="mobile-dropdown-container">
              <button
                className={`mobile-dropdown-btn ${isCategoryDropdownOpen ? 'open' : ''} ${showCascadeAnimations ? 'cascade-in' : ''}`}
                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
              >
                <span>{selectedCategory === 'home' ? 'CATEGORIES' : getCategoryDisplayName(selectedCategory)}</span>
                <img src={chevronDown} alt="" className="mobile-dropdown-chevron" />
              </button>

              {isCategoryDropdownOpen && (
                <div className="mobile-dropdown-menu">
                  {categories.map((category) => (
                    <button
                      key={category}
                      className={`mobile-dropdown-item ${selectedCategory === category ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedCategory(category);
                        setSelectedCategories(category === 'home' ? [] : [category]);
                        setExpandedCollectionId(null);
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      {getCategoryDisplayName(category)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {selectedCategory !== 'home' && (
              <>
                <div className={`mobile-header-vertical-divider ${showCascadeAnimations ? 'cascade-in' : ''}`} />

                <div className="mobile-dropdown-container">
                  <button
                    className={`mobile-dropdown-btn ${isMobileFiltersOpen ? 'active' : ''} ${showCascadeAnimations ? 'cascade-in' : ''}`}
                    onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
                  >
                    <span>SORT BY</span>
                    <img src={chevronDown} alt="" className="mobile-dropdown-chevron" />
                  </button>
                </div>
              </>
            )}
          </div>

          <div className={`mobile-header-divider ${showCascadeAnimations ? 'cascade-in-2' : ''}`} />
        </div>

      {/* Dropdown backdrop */}
      {isCategoryDropdownOpen && (
        <div
          className="mobile-dropdown-backdrop"
          onClick={() => setIsCategoryDropdownOpen(false)}
        />
      )}

      <div className="filter-button-container">
        <button
          className={`filter-button-mobile ${isMobileFiltersOpen ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''} ${showCascadeAnimations ? 'cascade-in' : ''}`}
          onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
        >
          <span className="filter-text">SORT</span>
          {hasActiveFilters && !isMobileFiltersOpen && (
            <span className="filter-badge"></span>
          )}
        </button>

        {/* Search Input - Mobile */}
        <div className={`mobile-header-search-container ${showCascadeAnimations ? 'cascade-in' : ''}`}>
          <input
            type="text"
            placeholder="SEARCH"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="mobile-header-search-input"
          />
          <img
            src={magnifyingGlass}
            alt="Search"
            className="mobile-header-search-icon"
          />
        </div>

        {/* Mobile Cart Button */}
        <button
          className={`mobile-cart-toggle-btn ${showCascadeAnimations ? 'cascade-in' : ''}`}
          onClick={toggleCart}
          aria-label="Open cart"
        >
          <span className="cart-text">
            CART ({formattedCartCount})
          </span>
        </button>
      </div>

      <div className="layout-container">
        {!hasActiveFilters ? (
          <div className={`products-container ${productsJustLoaded ? 'products-loaded fade-in' : ''}`}>
            <HomeCollections
              products={products}
              pricingType={pricingType}
              menuId={menuId}
              isMobile={isMobile}
              cart={cart}
              cartLoading={cartLoading}
              addingToCart={addingToCart}
              activeMarquees={activeMarquees}
              stoppingMarquees={stoppingMarquees}
              dealDescriptions={dealDescriptions}
              productTileCursorStyle={productTileCursorStyle}
              selectedTerpenes={selectedTerpenes}
              selectedCannabinoids={selectedCannabinoids}
              debugMode={debugMode}
              serverCollections={serverCollections}
              onProductClick={handleProductTileClick}
              onProductHover={handleProductTileHover}
              onProductLeave={handleProductTileLeave}
              onAddToCart={handleAddToCart}
              onDebugClick={handleDebugClick}
              onDealClick={handleDealClick}
              onVermontIncrement={handleVermontIncrement}
              onVermontDecrement={handleVermontDecrement}
              onViewAllClick={(category) => {
                const collection = serverCollections?.find(sc => sc.id === category);
                if (collection) {
                  setExpandedCollectionId(collection.id);
                  setSelectedCategory(collection.name);
                  setSelectedCategories([]);
                } else {
                  setExpandedCollectionId(null);
                  setSelectedCategory(category);
                  setSelectedCategories([category]);
                }
                setSelectedSort('default');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </div>
        ) : (
        <>
        {/* Mobile Filters Backdrop */}
        {isMobileFiltersOpen && (
          <div
            className="mobile-filters-backdrop"
            onClick={() => setIsMobileFiltersOpen(false)}
          />
        )}

        {/* Desktop Sidebar Filters */}
        <div className={`filters-sidebar ${isMobileFiltersOpen ? 'mobile-open' : ''} ${showCascadeAnimations ? 'reveal' : ''} ${process.env.REACT_APP_ENV === 'production' ? 'production-env' : ''}`}>
          {/* Mobile close button */}
          {isMobileFiltersOpen && (
            <button 
              className="mobile-filters-close"
              onClick={() => setIsMobileFiltersOpen(false)}
              aria-label="Close filters"
            >
              ✕
            </button>
          )}
          
          <div className="filters-sidebar-content">
            {/* Mobile Apply Filters Button */}
            {isMobileFiltersOpen && (
              <div className="mobile-apply-filters-section">
                <button
                  className="mobile-apply-filters-btn"
                  onClick={() => {
                    setIsMobileFiltersOpen(false);
                  }}
                >
                  <span>{hasActiveFilters ? 'Apply Filters' : 'Close Filters'}</span>
                </button>

                {hasActiveFilters && (
                  <button
                    className="mobile-clear-filters-btn"
                    onClick={() => {
                      setSelectedCategory('home');
                      setSelectedCategories([]);
                      setSelectedWeights([]);
                      setSelectedBrands([]);
                      setSelectedSubcategories([]);
                      setSelectedLineages([]);
                      setSelectedTerpenes([]);
                      setSelectedCannabinoids([]);
                      setThcRange([potencyRanges.thc.min, potencyRanges.thc.max]);
                      setCbdRange([potencyRanges.cbd.min, potencyRanges.cbd.max]);
                      setHasInteractedWithThc(false);
                      setHasInteractedWithCbd(false);
                      setSelectedEdibleThcValues([]);
                      setSelectedEdibleCbdValues([]);
                      setSelectedCannabinoidValues({});
                      setQuery('');
                      setSelectedSort('default');
                      clearingSpecialIdRef.current = true;
                      setSpecialId(null);
                      setSpecialProductIds(new Set());
                    }}
                  >
                    <span>CLEAR ALL FILTERS</span>
                  </button>
                )}
              </div>
            )}


            {/* Desktop Clear All Button - Only show when filters are active and not on mobile */}
            {!isMobileFiltersOpen && hasActiveFilters && (
              <div className={`filter-section clear-all-section ${showCascadeAnimations ? 'cascade-in' : ''}`}>
                <button
                  className="clear-all-btn"
                  onClick={() => {
                    setSelectedCategory('home');
                    setSelectedCategories([]);
                    setSelectedWeights([]);
                    setSelectedBrands([]);
                    setSelectedSubcategories([]);
                    setSelectedLineages([]);
                    setSelectedTerpenes([]);
                    setSelectedCannabinoids([]);
                    setThcRange([potencyRanges.thc.min, potencyRanges.thc.max]);
                    setCbdRange([potencyRanges.cbd.min, potencyRanges.cbd.max]);
                    setHasInteractedWithThc(false);
                    setHasInteractedWithCbd(false);
                    setSelectedEdibleThcValues([]);
                    setSelectedEdibleCbdValues([]);
                    setSelectedCannabinoidValues({});
                    setQuery('');
                    setSelectedSort('default');
                    clearingSpecialIdRef.current = true;
                    setSpecialId(null);
                    setSpecialProductIds(new Set());
                  }}
                >
                  <span>CLEAR ALL FILTERS</span>
                </button>
              </div>
            )}

            {/* Sort By Section */}
            <div className={`filter-section sort-filter-section ${showCascadeAnimations ? 'cascade-in' : ''}`}>
              <div
                className="filter-header"
                onClick={() => handleFilterToggle(isSortCollapsed, setIsSortCollapsed, setIsSortCollapsing)}
              >
                <span>SORT BY</span>
                <img
                  src={chevronDown}
                  alt="Toggle"
                  className={`chevron ${isSortCollapsed ? 'collapsed' : ''}`}
                />
              </div>

              {(!isSortCollapsed || isSortCollapsing) && (
                <div className={`filter-options ${isSortCollapsing ? 'collapsing' : ''}`}>
                  {getDynamicSortOptions().map((option) => (
                    <label key={option.value} className="filter-option">
                      <input
                        type="radio"
                        name="sort"
                        checked={selectedSort === option.value}
                        onChange={() => {
                          setSelectedSort(option.value);
                          // Track sort change
                          analyticsService.trackFilterApplied({
                            filter_type: 'sort',
                            filter_value: option.value,
                            results_count: filteredProducts.length,
                            store_id: menuId || 'unknown'
                          });
                        }}
                        className="radio-input"
                      />
                      <span className="radio-custom"></span>
                      <span className="option-text">{option.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Brands Section - with Sweetspot prioritized */}
            <div className={`filter-section brand-filter-section ${showCascadeAnimations ? 'cascade-in' : ''}`}>
              <div 
                className="filter-header"
                onClick={() => handleFilterToggle(isBrandCollapsed, setIsBrandCollapsed, setIsBrandCollapsing)}
              >
                <span>BRANDS</span>
                <img 
                  src={chevronDown} 
                  alt="Toggle" 
                  className={`chevron ${isBrandCollapsed ? 'collapsed' : ''}`}
                />
              </div>
              
              {(!isBrandCollapsed || isBrandCollapsing) && (
                <div className={`filter-options ${isBrandCollapsing ? 'collapsing' : ''}`}>
                  {(() => {
                    // Sort brands to put Sweetspot first (after "all")
                    const sortedBrands = [...brands];

                    const sweetspotIndex = sortedBrands.findIndex(brand =>
                      brand.toLowerCase().includes('sweetspot') || brand.toLowerCase().includes('sweet spot')
                    );

                    if (sweetspotIndex > 1) { // If found and not already at position 1 (after "all")
                      const sweetspotBrand = sortedBrands.splice(sweetspotIndex, 1)[0];
                      sortedBrands.splice(1, 0, sweetspotBrand); // Insert after "all"
                    }

                    // Filter out 'all' option since we now support multiple selection
                    const brandsToShow = sortedBrands.filter(brand => brand !== 'all');

                    return brandsToShow.map(brand => (
                      <label key={brand} className="filter-option">
                        <input
                          type="checkbox"
                          checked={selectedBrands.some(b => b.toLowerCase() === brand.toLowerCase())}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedBrands(prev => [...prev, brand]);
                              // Track brand filter applied
                              attributionService.trackFilterApplication('brand', brand);
                              const attributionData = attributionService.getAttributionData();

                              analyticsService.trackFilterApplied({
                                filter_type: 'brand',
                                filter_value: brand,
                                results_count: filteredProducts.length,
                                store_id: menuId || 'unknown',
                                ...attributionData.utm_attribution,
                                session_id: attributionData.sessionId,
                                filter_sequence: attributionData.filter_attribution.length
                              });
                            } else {
                              setSelectedBrands(prev => prev.filter(b => b !== brand));
                              // Track brand filter removed
                              analyticsService.trackFilterApplied({
                                filter_type: 'brand_removed',
                                filter_value: brand,
                                results_count: filteredProducts.length,
                                store_id: menuId || 'unknown'
                              });
                            }
                          }}
                          className="checkbox-input"
                        />
                        <span className="checkbox-custom checkbox-circular"></span>
                        <span className="option-text">
                          {brand}
                        </span>
                      </label>
                    ));
                  })()}
                </div>
              )}
            </div>
            
            {/* Lineage Section */}
            {/* Hide LINEAGE filter for accessories and apparel */}
            {selectedCategory.toLowerCase() !== 'accessory' && selectedCategory.toLowerCase() !== 'accessories' && selectedCategory.toLowerCase() !== 'apparel' && (
            <div className={`filter-section ${showCascadeAnimations ? 'cascade-in' : ''}`}>
              <div
                className="filter-header"
                onClick={() => handleFilterToggle(isLineageCollapsed, setIsLineageCollapsed, setIsLineageCollapsing)}
              >
                <span>LINEAGE</span>
                <img
                  src={chevronDown}
                  alt="Toggle"
                  className={`chevron ${isLineageCollapsed ? 'collapsed' : ''}`}
                />
              </div>

              {(!isLineageCollapsed || isLineageCollapsing) && (
                <div className={`filter-options ${isLineageCollapsing ? 'collapsing' : ''}`}>
                  {/* Filter out 'all' option since we now support multiple selection */}
                  {lineages.filter(lineage => lineage !== 'all').map(lineage => (
                    <label key={lineage} className="filter-option">
                      <input
                        type="checkbox"
                        checked={selectedLineages.includes(lineage)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLineages(prev => [...prev, lineage]);
                            // Track lineage filter applied
                            analyticsService.trackFilterApplied({
                              filter_type: 'lineage',
                              filter_value: formatLineageForDisplay(lineage),
                              results_count: filteredProducts.length,
                              store_id: menuId || 'unknown'
                            });
                          } else {
                            setSelectedLineages(prev => prev.filter(l => l !== lineage));
                            // Track lineage filter removed
                            analyticsService.trackFilterApplied({
                              filter_type: 'lineage_removed',
                              filter_value: formatLineageForDisplay(lineage),
                              results_count: filteredProducts.length,
                              store_id: menuId || 'unknown'
                            });
                          }
                        }}
                        className="checkbox-input"
                      />
                      <span className="checkbox-custom checkbox-circular"></span>
                      <span className="option-text">
                        {formatLineageForDisplay(lineage)}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            )}
            
            {/* Only show weight filter if there are actual weight options */}
            {/* For Vermont: hide weight filter for ALL and FLOWER categories (they use increment system) */}
            {weights.length > 1 && !(menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC && (selectedCategory.toLowerCase() === 'home' || selectedCategory.toLowerCase() === 'flower')) && (
              <div className={`filter-section ${showCascadeAnimations ? 'cascade-in' : ''}`}>
                <div 
                  className="filter-header"
                  onClick={() => handleFilterToggle(isWeightCollapsed, setIsWeightCollapsed, setIsWeightCollapsing)}
                >
                  <span>WEIGHTS</span>
                  <img 
                    src={chevronDown} 
                    alt="Toggle" 
                    className={`chevron ${isWeightCollapsed ? 'collapsed' : ''}`}
                  />
                </div>
                
                {(!isWeightCollapsed || isWeightCollapsing) && (
                  <div className={`filter-options ${isWeightCollapsing ? 'collapsing' : ''}`}>
                    {/* Filter out 'all' option since we now support multiple selection */}
                    {weights.filter(weight => weight !== 'all').map(weight => (
                      <label key={weight} className="filter-option">
                        <input
                          type="checkbox"
                          checked={selectedWeights.includes(weight)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedWeights(prev => [...prev, weight]);
                              // Track weight filter applied
                              analyticsService.trackFilterApplied({
                                filter_type: 'weight',
                                filter_value: weight,
                                results_count: filteredProducts.length,
                                store_id: menuId || 'unknown'
                              });
                            } else {
                              setSelectedWeights(prev => prev.filter(w => w !== weight));
                              // Track weight filter removed
                              analyticsService.trackFilterApplied({
                                filter_type: 'weight_removed',
                                filter_value: weight,
                                results_count: filteredProducts.length,
                                store_id: menuId || 'unknown'
                              });
                            }
                          }}
                          className="checkbox-input"
                        />
                        <span className="checkbox-custom checkbox-circular"></span>
                        <span className="option-text">
                          {weight}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Terpenes Section - Hide when edibles, accessories, or apparel category is selected or no terpene data available */}
            {selectedCategory.toLowerCase() !== 'edibles' && selectedCategory.toLowerCase() !== 'accessory' && selectedCategory.toLowerCase() !== 'accessories' && selectedCategory.toLowerCase() !== 'apparel' && terpenes.length > 1 && (
            <div className={`filter-section ${showCascadeAnimations ? 'cascade-in' : ''}`}>
              <div 
                className="filter-header"
                onClick={() => handleFilterToggle(isTerpeneCollapsed, setIsTerpeneCollapsed, setIsTerpeneCollapsing)}
              >
                <span>TERPENES</span>
                <img 
                  src={chevronDown} 
                  alt="Toggle" 
                  className={`chevron ${isTerpeneCollapsed ? 'collapsed' : ''}`}
                />
              </div>
              
              {(!isTerpeneCollapsed || isTerpeneCollapsing) && (
                <div className={`filter-options ${isTerpeneCollapsing ? 'collapsing' : ''}`}>
                  {/* Filter out 'all' option since we now support multiple selection */}
                  {terpenes.filter(terpene => terpene !== 'all').map(terpene => (
                    <label key={terpene} className="filter-option">
                      <input
                        type="checkbox"
                        checked={selectedTerpenes.includes(terpene)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTerpenes(prev => [...prev, terpene]);
                            // Track terpene filter applied
                            analyticsService.trackFilterApplied({
                              filter_type: 'terpene',
                              filter_value: terpene,
                              results_count: filteredProducts.length,
                              store_id: menuId || 'unknown'
                            });
                          } else {
                            setSelectedTerpenes(prev => prev.filter(t => t !== terpene));
                            // Track terpene filter removed
                            analyticsService.trackFilterApplied({
                              filter_type: 'terpene_removed',
                              filter_value: terpene,
                              results_count: filteredProducts.length,
                              store_id: menuId || 'unknown'
                            });
                          }
                        }}
                        className="checkbox-input"
                      />
                      <span className="checkbox-custom checkbox-circular"></span>
                      <span className="option-text">
                        {terpene}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            )}
            
            {/* Cannabinoids Section - Hide when edibles, accessories, or apparel category is selected */}
            {selectedCategory.toLowerCase() !== 'edibles' && selectedCategory.toLowerCase() !== 'accessory' && selectedCategory.toLowerCase() !== 'accessories' && selectedCategory.toLowerCase() !== 'apparel' && (
            <div className={`filter-section ${showCascadeAnimations ? 'cascade-in' : ''}`}>
              <div 
                className="filter-header"
                onClick={() => handleFilterToggle(isCannabinoidCollapsed, setIsCannabinoidCollapsed, setIsCannabinoidCollapsing)}
              >
                <span>CANNABINOIDS</span>
                <img 
                  src={chevronDown} 
                  alt="Toggle" 
                  className={`chevron ${isCannabinoidCollapsed ? 'collapsed' : ''}`}
                />
              </div>
              
              {(!isCannabinoidCollapsed || isCannabinoidCollapsing) && (
                <div className={`filter-options ${isCannabinoidCollapsing ? 'collapsing' : ''}`}>
                  {/* Filter out 'all' option since we now support multiple selection */}
                  {cannabinoids.filter(cannabinoid => cannabinoid !== 'all').map(cannabinoid => (
                    <label key={cannabinoid} className="filter-option">
                      <input
                        type="checkbox"
                        checked={selectedCannabinoids.includes(cannabinoid)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCannabinoids(prev => [...prev, cannabinoid]);
                            // Track cannabinoid filter applied
                            analyticsService.trackFilterApplied({
                              filter_type: 'cannabinoid',
                              filter_value: cannabinoid,
                              results_count: filteredProducts.length,
                              store_id: menuId || 'unknown'
                            });
                          } else {
                            setSelectedCannabinoids(prev => prev.filter(c => c !== cannabinoid));
                            // Track cannabinoid filter removed
                            analyticsService.trackFilterApplied({
                              filter_type: 'cannabinoid_removed',
                              filter_value: cannabinoid,
                              results_count: filteredProducts.length,
                              store_id: menuId || 'unknown'
                            });
                          }
                        }}
                        className="checkbox-input"
                      />
                      <span className="checkbox-custom checkbox-circular"></span>
                      <span className="option-text">
                        {cannabinoid}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            )}
            
            {/* Potency Filter Section - Hide when in edibles, accessories, or apparel category */}
            {selectedCategory.toLowerCase() !== 'edibles' && selectedCategory.toLowerCase() !== 'accessory' && selectedCategory.toLowerCase() !== 'accessories' && selectedCategory.toLowerCase() !== 'apparel' && (
            <div className={`filter-section ${showCascadeAnimations ? 'cascade-in' : ''}`}>
              <div 
                className="filter-header"
                onClick={() => handleFilterToggle(isPotencyCollapsed, setIsPotencyCollapsed, setIsPotencyCollapsing)}
              >
                <span>POTENCY</span>
                <img 
                  src={chevronDown} 
                  alt="Toggle" 
                  className={`chevron ${isPotencyCollapsed ? 'collapsed' : ''}`}
                />
              </div>
              
              {(!isPotencyCollapsed || isPotencyCollapsing) && (
                <div className={`filter-options ${isPotencyCollapsing ? 'collapsing' : ''}`}>
                  {/* THC Slider */}
                  <div className="potency-slider-container">
                    <h4 className="potency-label">THC: {potencyRanges.thc.step === 0.1 ? thcRange[0].toFixed(2) : thcRange[0]}% - {potencyRanges.thc.step === 0.1 ? thcRange[1].toFixed(2) : thcRange[1]}%</h4>
                    <div className="range-slider-container">
                      <div className="range-slider"></div>
                      <div 
                        className="slider-track" 
                        style={{
                          left: `${potencyRanges.thc.max - potencyRanges.thc.min > 0 ? Math.max(0, Math.min(100, ((thcRange[0] - potencyRanges.thc.min) / (potencyRanges.thc.max - potencyRanges.thc.min)) * 100)) : 0}%`,
                          width: `${potencyRanges.thc.max - potencyRanges.thc.min > 0 ? Math.max(0, Math.min(100, ((thcRange[1] - thcRange[0]) / (potencyRanges.thc.max - potencyRanges.thc.min)) * 100)) : 100}%`
                        }}
                      ></div>
                      <input
                        type="range"
                        min={potencyRanges.thc.min}
                        max={potencyRanges.thc.max}
                        step={potencyRanges.thc.step}
                        value={thcRange[0]}
                        aria-label="Minimum THC percentage"
                        aria-valuemin={potencyRanges.thc.min} 
                        aria-valuemax={potencyRanges.thc.max}
                        aria-valuenow={thcRange[0]}
                        onChange={(e) => {
                          const newValue = potencyRanges.thc.step === 0.1 ? parseFloat(e.target.value) : parseInt(e.target.value);
                          // Ensure minimum gap of 0.2 for decimals or 5 for whole numbers
                          const minGap = potencyRanges.thc.step === 0.1 ? 0.2 : 5;
                          setThcRange(prev => [Math.min(newValue, prev[1] - minGap), prev[1]]);
                          setHasInteractedWithThc(true);
                        }}
                      />
                      <input
                        type="range"
                        min={potencyRanges.thc.min}
                        max={potencyRanges.thc.max}
                        step={potencyRanges.thc.step}
                        value={thcRange[1]}
                        aria-label="Maximum THC percentage"
                        aria-valuemin={potencyRanges.thc.min} 
                        aria-valuemax={potencyRanges.thc.max}
                        aria-valuenow={thcRange[1]}
                        onChange={(e) => {
                          const newValue = potencyRanges.thc.step === 0.1 ? parseFloat(e.target.value) : parseInt(e.target.value);
                          // Ensure minimum gap of 0.2 for decimals or 5 for whole numbers
                          const minGap = potencyRanges.thc.step === 0.1 ? 0.2 : 5;
                          setThcRange(prev => [prev[0], Math.max(newValue, prev[0] + minGap)]);
                          setHasInteractedWithThc(true);
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* CBD Slider - Positioned below THC */}
                  <div className="potency-slider-container">
                    <h4 className="potency-label">CBD: {potencyRanges.cbd.step === 0.1 ? cbdRange[0].toFixed(2) : cbdRange[0]}% - {potencyRanges.cbd.step === 0.1 ? cbdRange[1].toFixed(2) : cbdRange[1]}%</h4>
                    <div className="range-slider-container">
                      <div className="range-slider"></div>
                      <div 
                        className="slider-track" 
                        style={{
                          left: `${potencyRanges.cbd.max - potencyRanges.cbd.min > 0 ? Math.max(0, Math.min(100, ((cbdRange[0] - potencyRanges.cbd.min) / (potencyRanges.cbd.max - potencyRanges.cbd.min)) * 100)) : 0}%`,
                          width: `${potencyRanges.cbd.max - potencyRanges.cbd.min > 0 ? Math.max(0, Math.min(100, ((cbdRange[1] - cbdRange[0]) / (potencyRanges.cbd.max - potencyRanges.cbd.min)) * 100)) : 100}%`
                        }}
                      ></div>
                      <input
                        type="range"
                        min={potencyRanges.cbd.min}
                        max={potencyRanges.cbd.max}
                        step={potencyRanges.cbd.step}
                        value={cbdRange[0]}
                        aria-label="Minimum CBD percentage"
                        aria-valuemin={potencyRanges.cbd.min} 
                        aria-valuemax={potencyRanges.cbd.max}
                        aria-valuenow={cbdRange[0]}
                        onChange={(e) => {
                          const newValue = potencyRanges.cbd.step === 0.1 ? parseFloat(e.target.value) : parseInt(e.target.value);
                          // Ensure minimum gap of 0.2 for decimals or 5 for whole numbers
                          const minGap = potencyRanges.cbd.step === 0.1 ? 0.2 : 5;
                          setCbdRange(prev => [Math.min(newValue, prev[1] - minGap), prev[1]]);
                          setHasInteractedWithCbd(true);
                        }}
                      />
                      <input
                        type="range"
                        min={potencyRanges.cbd.min}
                        max={potencyRanges.cbd.max}
                        step={potencyRanges.cbd.step}
                        value={cbdRange[1]}
                        aria-label="Maximum CBD percentage"
                        aria-valuemin={potencyRanges.cbd.min} 
                        aria-valuemax={potencyRanges.cbd.max}
                        aria-valuenow={cbdRange[1]}
                        onChange={(e) => {
                          const newValue = potencyRanges.cbd.step === 0.1 ? parseFloat(e.target.value) : parseInt(e.target.value);
                          // Ensure minimum gap of 0.2 for decimals or 5 for whole numbers
                          const minGap = potencyRanges.cbd.step === 0.1 ? 0.2 : 5;
                          setCbdRange(prev => [prev[0], Math.max(newValue, prev[0] + minGap)]);
                          setHasInteractedWithCbd(true);
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Subcategory Filter Section */}
            {subcategories.length > 0 && (
            <div className={`filter-section ${showCascadeAnimations ? 'cascade-in' : ''}`}>
              <div
                className="filter-header"
                onClick={() => handleFilterToggle(isSubcategoryCollapsed, setIsSubcategoryCollapsed, setIsSubcategoryCollapsing)}
              >
                <span>SUBCATEGORY</span>
                <img
                  src={chevronDown}
                  alt="Toggle"
                  className={`chevron ${isSubcategoryCollapsed ? 'collapsed' : ''}`}
                />
              </div>

              {(!isSubcategoryCollapsed || isSubcategoryCollapsing) && (
                <div className={`filter-options ${isSubcategoryCollapsing ? 'collapsing' : ''}`}>
                  {subcategories.map(subcategory => (
                    <label key={subcategory} className="filter-option">
                      <input
                        type="checkbox"
                        checked={selectedSubcategories.includes(subcategory)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSubcategories(prev => [...prev, subcategory]);
                          } else {
                            setSelectedSubcategories(prev => prev.filter(s => s !== subcategory));
                          }
                        }}
                        className="checkbox-input"
                      />
                      <span className="checkbox-custom checkbox-circular"></span>
                      <span className="option-text">{formatSubcategory(subcategory)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Dose Filter Section - Only show in edibles category */}
            {selectedCategory.toLowerCase() === 'edibles' && (
              <div className={`filter-section ${showCascadeAnimations ? 'cascade-in' : ''}`}>
                <div 
                  className="filter-header"
                  onClick={() => handleFilterToggle(isEdiblePotencyCollapsed, setIsEdiblePotencyCollapsed, setIsEdiblePotencyCollapsing)}
                >
                  <span>DOSE</span>
                  <img 
                    src={chevronDown} 
                    alt="Toggle" 
                    className={`chevron ${isEdiblePotencyCollapsed ? 'collapsed' : ''}`}
                  />
                </div>
                
                {(!isEdiblePotencyCollapsed || isEdiblePotencyCollapsing) && (
                  <div className={`filter-options ${isEdiblePotencyCollapsing ? 'collapsing' : ''}`}>
                    {/* THC Options */}
                    {mgPotencyValues.thc.length > 0 && (
                      <>
                        <div className="dose-section-header">THC:</div>
                        <div className="dose-options">
                          {mgPotencyValues.thc.map(value => (
                            <label key={`thc-${value}`} className="filter-option">
                              <input
                                type="checkbox"
                                checked={selectedEdibleThcValues.includes(value)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedEdibleThcValues(prev => [...prev, value]);
                                  } else {
                                    setSelectedEdibleThcValues(prev => prev.filter(v => v !== value));
                                  }
                                }}
                                className="checkbox-input"
                              />
                              <span className="checkbox-custom"></span>
                              <span className="option-text">{value}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {/* CBD Options */}
                    {mgPotencyValues.cbd.length > 0 && (
                      <>
                        <div className="dose-section-header">CBD:</div>
                        <div className="dose-options">
                          {mgPotencyValues.cbd.map(value => (
                            <label key={`cbd-${value}`} className="filter-option">
                              <input
                                type="checkbox"
                                checked={selectedEdibleCbdValues.includes(value)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedEdibleCbdValues(prev => [...prev, value]);
                                  } else {
                                    setSelectedEdibleCbdValues(prev => prev.filter(v => v !== value));
                                  }
                                }}
                                className="checkbox-input"
                              />
                              <span className="checkbox-custom"></span>
                              <span className="option-text">{value}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {/* Dynamic Cannabinoid Options */}
                    {Object.entries(mgPotencyValues.cannabinoids || {}).map(([cannabinoidName, values]) => (
                      values.length > 0 && (
                        <React.Fragment key={cannabinoidName}>
                          <div className="dose-section-header">{cannabinoidName}:</div>
                          <div className="dose-options">
                            {values.map(value => (
                              <label key={`${cannabinoidName}-${value}`} className="filter-option">
                                <input
                                  type="checkbox"
                                  checked={(selectedCannabinoidValues[cannabinoidName] || []).includes(value)}
                                  onChange={(e) => {
                                    setSelectedCannabinoidValues(prev => {
                                      const current = prev[cannabinoidName] || [];
                                      if (e.target.checked) {
                                        return {
                                          ...prev,
                                          [cannabinoidName]: [...current, value]
                                        };
                                      } else {
                                        const updated = current.filter(v => v !== value);
                                        if (updated.length === 0) {
                                          const { [cannabinoidName]: removed, ...rest } = prev;
                                          return rest;
                                        }
                                        return {
                                          ...prev,
                                          [cannabinoidName]: updated
                                        };
                                      }
                                    });
                                  }}
                                  className="checkbox-input"
                                />
                                <span className="checkbox-custom"></span>
                                <span className="option-text">{value}</span>
                              </label>
                            ))}
                          </div>
                        </React.Fragment>
                      )
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Product Count - Desktop Filters */}
            <div className={`desktop-product-count ${showCascadeAnimations ? 'cascade-in' : ''}`}>
              <div className="product-detail-row">
                PRODUCTS: <span className="product-detail-value">{productCards.length}</span>
              </div>
            </div>

          </div>
        </div>

        {/* Products Grid Container */}
        <div className={`products-container ${isMobileFiltersOpen ? 'mobile-hidden' : ''} ${productsJustLoaded ? 'products-loaded fade-in' : ''}`}>
          <div className={`product-grid ${productCards.length === 0 ? 'empty-state' : ''}`}>
            {productCards.length > 0 ? (
              productCards.map(({ id, product, variant }) => {
                const animationState = animatingProducts.get(id);
                // Apply filtering transition classes only:
                // - 'in' products animate with filtering-in
                // - 'out' products animate with filtering-out
                // - all other products render with no animation class
                const animationClass = animationState === 'in' ? 'filtering-in' : animationState === 'out' ? 'filtering-out' : '';

                return (
                <ProductTileV3
                  key={id}
                  product={product}
                  variant={variant}
                  tileId={id}
                  animationClass={animationClass}
                  pricingType={pricingType}
                  menuId={menuId}
                  selectedCategory={selectedCategory}
                  isVermontStore={menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC}
                  debugMode={debugMode}
                  cart={cart}
                  cartLoading={cartLoading}
                  isAddingToCart={addingToCart.has(`${product.id}-${variant.id}`) || addingToCart.has(`vermont-${product.id}`)}
                  activeMarquees={activeMarquees}
                  stoppingMarquees={stoppingMarquees}
                  dealDescriptions={dealDescriptions}
                  productTileCursorStyle={productTileCursorStyle}
                  isMobile={isMobile}
                  selectedTerpenes={selectedTerpenes}
                  selectedCannabinoids={selectedCannabinoids}
                  onProductClick={handleProductTileClick}
                  onProductHover={handleProductTileHover}
                  onProductLeave={handleProductTileLeave}
                  onAddToCart={handleAddToCart}
                  onDebugClick={handleDebugClick}
                  onDealClick={handleDealClick}
                  onVermontIncrement={handleVermontIncrement}
                  onVermontDecrement={handleVermontDecrement}
                />
                );
              })
            ) : (
              // Create placeholder grid items to maintain structure
              <>
                {/* First grid item with empty message */}
                <div className="product-tile empty-state-tile">
                  <div className="empty-state-content">
                    <div className="no-results-message">
                      {query.trim() ? `No products found matching "${query}"` : 'No products available'}
                    </div>
                  </div>
                </div>
                
                {/* Additional placeholder items to maintain grid structure */}
                {[...Array(5)].map((_, index) => (
                  <div key={`placeholder-${index}`} className="product-tile placeholder-tile">
                    <div className="placeholder-content"></div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
        </>
        )}
      </div>

      {/* DEBUG MODE - Debug popup */}
      {debugMode && showDebugPopup && debugProduct && (
        <div className="debug-popup-overlay" onClick={() => setShowDebugPopup(false)}>
          <div className="debug-popup" onClick={(e) => e.stopPropagation()}>
            <div className="debug-popup-header">
              <h3>Product Debug Data</h3>
              <button 
                className="debug-popup-close"
                onClick={() => setShowDebugPopup(false)}
                aria-label="Close debug popup"
              >
                ×
              </button>
            </div>
            
            <div className="debug-popup-content">
              <div className="debug-section">
                <h4>Basic Information</h4>
                <div className="debug-row"><span className="debug-label">ID:</span> {debugProduct.id}</div>
                <div className="debug-row"><span className="debug-label">Name:</span> {debugProduct.name}</div>
                <div className="debug-row"><span className="debug-label">Description:</span> {debugProduct.description || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Category:</span> {debugProduct.category}</div>
                <div className="debug-row"><span className="debug-label">Subcategory:</span> {debugProduct.subcategory || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Created At:</span> {debugProduct.createdAt || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Enterprise Product ID:</span> {debugProduct.enterpriseProductId || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Product Batch ID:</span> {debugProduct.productBatchId || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Slug:</span> {debugProduct.slug || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">POS ID:</span> {debugProduct.posId || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Staff Pick:</span> {debugProduct.staffPick ? 'Yes' : 'No'}</div>
                <div className="debug-row"><span className="debug-label">Tags:</span> {debugProduct.tags?.join(', ') || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Menu Types:</span> {debugProduct.menuTypes?.join(', ') || 'N/A'}</div>
              </div>

              <div className="debug-section">
                <h4>Brand Information</h4>
                <div className="debug-row"><span className="debug-label">Brand ID:</span> {debugProduct.brand?.id || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Brand Name:</span> {debugProduct.brand?.name || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Brand Description:</span> {debugProduct.brand?.description || 'N/A'}</div>
              </div>

              <div className="debug-section">
                <h4>Strain & Classification</h4>
                <div className="debug-row"><span className="debug-label">Strain Type:</span> {debugProduct.strainType || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Effects:</span> {debugProduct.effects?.join(', ') || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Flavors:</span> {debugProduct.flavors?.join(', ') || 'N/A'}</div>
              </div>

              <div className="debug-section">
                <h4>Terpenes Detail</h4>
                {debugProduct.terpenes && debugProduct.terpenes.length > 0 ? (
                  debugProduct.terpenes.map((terpene, index) => (
                    <div key={index} className="debug-variant">
                      <h5>Terpene {index + 1}</h5>
                      <div className="debug-row"><span className="debug-label">ID:</span> {terpene.id || 'N/A'}</div>
                      <div className="debug-row"><span className="debug-label">Name:</span> {terpene.name || 'N/A'}</div>
                      <div className="debug-row"><span className="debug-label">Description:</span> {terpene.description || 'N/A'}</div>
                      <div className="debug-row"><span className="debug-label">Value:</span> {terpene.value} {terpene.unitSymbol || ''}</div>
                    </div>
                  ))
                ) : (
                  <div className="debug-row">No terpene data available</div>
                )}
              </div>

              <div className="debug-section">
                <h4>Cannabinoids Detail</h4>
                {debugProduct.cannabinoids && debugProduct.cannabinoids.length > 0 ? (
                  debugProduct.cannabinoids.map((cannabinoid, index) => (
                    <div key={index} className="debug-variant">
                      <h5>Cannabinoid {index + 1}</h5>
                      <div className="debug-row"><span className="debug-label">ID:</span> {cannabinoid.id || 'N/A'}</div>
                      <div className="debug-row"><span className="debug-label">Name:</span> {cannabinoid.name || 'N/A'}</div>
                      <div className="debug-row"><span className="debug-label">Description:</span> {cannabinoid.description || 'N/A'}</div>
                      <div className="debug-row"><span className="debug-label">Value:</span> {cannabinoid.value} {cannabinoid.unitSymbol || ''}</div>
                    </div>
                  ))
                ) : (
                  <div className="debug-row">No cannabinoid data available</div>
                )}
              </div>

              <div className="debug-section">
                <h4>Potency Information</h4>
                <div className="debug-row"><span className="debug-label">THC Formatted:</span> {debugProduct.potencyThc?.formatted || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">THC Range:</span> {debugProduct.potencyThc?.range?.join(', ') || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">THC Unit:</span> {debugProduct.potencyThc?.unit || 'N/A'}</div>
                <div className="debug-row">
                  <span className="debug-label">Copy THC JSON:</span> 
                  <button 
                    className="copy-json-btn"
                    onClick={() => {
                      const thcJson = JSON.stringify(debugProduct.potencyThc, null, 2);
                      navigator.clipboard.writeText(thcJson).catch(() => {
                        // Clipboard copy failed silently
                      });
                    }}
                  >
                    Copy THC JSON
                  </button>
                </div>
                <div className="debug-row"><span className="debug-label">CBD Formatted:</span> {debugProduct.potencyCbd?.formatted || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">CBD Range:</span> {debugProduct.potencyCbd?.range?.join(', ') || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">CBD Unit:</span> {debugProduct.potencyCbd?.unit || 'N/A'}</div>
              </div>

              <div className="debug-section">
                <h4>Images</h4>
                <div className="debug-row"><span className="debug-label">Image Count:</span> {debugProduct.images?.length || 0}</div>
                {debugProduct.images?.map((img, index) => (
                  <div key={index} className="debug-row">
                    <span className="debug-label">Image {index + 1}:</span> {img.url}
                  </div>
                ))}
              </div>

              <div className="debug-section">
                <h4>Variants & Pricing</h4>
                {debugProduct.variants?.map((variant, index) => (
                  <div key={index} className="debug-variant">
                    <h5>Variant {index + 1}</h5>
                    <div className="debug-row"><span className="debug-label">ID:</span> {variant.id}</div>
                    <div className="debug-row"><span className="debug-label">Option:</span> {variant.option}</div>
                    <div className="debug-row"><span className="debug-label">Price Med:</span> ${variant.priceMed?.toFixed(2) || 'N/A'}</div>
                    <div className="debug-row"><span className="debug-label">Price Rec:</span> ${variant.priceRec?.toFixed(2) || 'N/A'}</div>
                    <div className="debug-row"><span className="debug-label">Special Price Med:</span> ${variant.specialPriceMed?.toFixed(2) || 'N/A'}</div>
                    <div className="debug-row"><span className="debug-label">Special Price Rec:</span> ${variant.specialPriceRec?.toFixed(2) || 'N/A'}</div>
                    <div className="debug-row"><span className="debug-label">Quantity:</span> {variant.quantity}</div>
                    <div className="debug-row"><span className="debug-label">Special Condition IDs:</span> {variant.specialConditionIds?.join(', ') || 'N/A'}</div>
                    <div className="debug-row">
                      <span className="debug-label">Special Reward IDs:</span> 
                      <RewardDetails rewardIds={variant.specialRewardIds || []} />
                    </div>
                    <div className="debug-row"><span className="debug-label">Flower Equivalent:</span> {variant.flowerEquivalent ? `${variant.flowerEquivalent.value} ${variant.flowerEquivalent.unit}` : 'N/A'}</div>
                  </div>
                )) || <div>No variants available</div>}
              </div>

              <div className="debug-section">
                <h4>POS Metadata (Enhanced)</h4>
                <div className="debug-row"><span className="debug-label">POS ID:</span> {debugProduct.posMetaData?.id || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Category:</span> {debugProduct.posMetaData?.category || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">SKU:</span> {debugProduct.posMetaData?.sku || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Batch ID:</span> {debugProduct.posMetaData?.batchId || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Batch Name:</span> {debugProduct.posMetaData?.batchName || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Alternate Name:</span> {debugProduct.posMetaData?.alternateName || 'N/A'}</div>
                <div className="debug-row"><span className="debug-label">Lab Result URL:</span> 
                  {debugProduct.posMetaData?.labResultUrl ? (
                    <a href={debugProduct.posMetaData.labResultUrl} target="_blank" rel="noopener noreferrer">
                      {debugProduct.posMetaData.labResultUrl}
                    </a>
                  ) : 'N/A'}
                </div>
                <div className="debug-row"><span className="debug-label">Packaged Date:</span> {debugProduct.posMetaData?.packagedDate || 'N/A'}</div>
              </div>

              <div className="debug-section">
                <h4>Product Availability by Retailer</h4>
                {debugProduct.productAvailabilityByRetailer && debugProduct.productAvailabilityByRetailer.length > 0 ? (
                  debugProduct.productAvailabilityByRetailer.map((availability, index) => (
                    <div key={index} className="debug-variant">
                      <h5>Retailer {index + 1}</h5>
                      <div className="debug-row"><span className="debug-label">Retailer ID:</span> {availability.retailer.id}</div>
                      <div className="debug-row"><span className="debug-label">Retailer Name:</span> {availability.retailer.name}</div>
                      <div className="debug-row"><span className="debug-label">Product ID:</span> {availability.productId}</div>
                      <div className="debug-row"><span className="debug-label">Available:</span> {availability.isAvailable ? 'Yes' : 'No'}</div>
                    </div>
                  ))
                ) : (
                  <div className="debug-row">No retailer availability data</div>
                )}
              </div>

              <div className="debug-section">
                <h4>Raw JSON Data</h4>
                <pre className="debug-json">{JSON.stringify(debugProduct, null, 2)}</pre>
              </div>

              <div className="debug-section">
                <h4>Raw Dutchie Response</h4>
                <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px' }}>
                  Debug info: _dutchieRaw exists = {debugProduct._dutchieRaw ? 'YES' : 'NO'},
                  Available keys: {Object.keys(debugProduct || {}).filter(k => k.startsWith('_')).join(', ') || 'none starting with _'}
                </div>
                <pre className="debug-json">
                  {debugProduct._dutchieRaw
                    ? JSON.stringify(debugProduct._dutchieRaw, null, 2)
                    : 'No raw Dutchie data available - check debug info above'}
                </pre>
              </div>
            </div>
            
            <div className="debug-popup-footer">
              <button 
                className="debug-close-button"
                onClick={() => setShowDebugPopup(false)}
              >
                Close Debug Window
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DEAL MODE - Deal popup */}
      {showDealPopup && dealProduct && !dealLoading && dealRewards.length > 0 && (
        <div className="deal-popup-overlay" onClick={() => setShowDealPopup(false)}>
          <div className="deal-popup" onClick={(e) => e.stopPropagation()}>
            <div className="deal-popup-header">
              <h3>{dealRewards[0].name}</h3>
            </div>
            
            <div className="deal-popup-content">
              {/* Show Rewards with name as title and description as body */}
              <div className="deal-section">
                {dealRewards.map((reward, index) => (
                  <div key={reward.id || index} className="deal-item">
                    {reward.description && (
                      <div className="deal-description">
                        {reward.description.replace(/\n+/g, '\n').trim()}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Show Special Prices */}
              {dealProduct && hasSpecialPrices(dealProduct) && (
                <div className="deal-section">
                  <h4 className="deal-section-title">💰 Special Pricing</h4>
                  {dealProduct.variants
                    .filter(variant => variant.specialPriceMed !== null || variant.specialPriceRec !== null)
                    .map((variant, index) => (
                      <div key={variant.id || index} className="deal-item special-price-item">
                        <div className="deal-name">{variant.option}</div>
                        
                        {/* Medical Pricing */}
                        {variant.specialPriceMed !== null && variant.priceMed !== null && (
                          <div className="price-comparison">
                            <span className="price-label">Medical:</span>
                            <span className="regular-price">${variant.priceMed.toFixed(2)}</span>
                            <span className="special-price">${variant.specialPriceMed.toFixed(2)}</span>
                            <span className="discount-percentage">
                              {calculateDiscount(variant.priceMed, variant.specialPriceMed)}% OFF
                            </span>
                          </div>
                        )}

                        {/* Recreational Pricing */}
                        {variant.specialPriceRec !== null && variant.priceRec !== null && (
                          <div className="price-comparison">
                            <span className="price-label">Recreational:</span>
                            <span className="regular-price">${variant.priceRec.toFixed(2)}</span>
                            <span className="special-price">${variant.specialPriceRec.toFixed(2)}</span>
                            <span className="discount-percentage">
                              {calculateDiscount(variant.priceRec, variant.specialPriceRec)}% OFF
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}

              {/* No deals available */}
              {!dealLoading && dealRewards.length === 0 && (!dealProduct || (!hasRewards(dealProduct) && !hasSpecialPrices(dealProduct))) && (
                <div className="deal-loading">
                  <div className="deal-loading-text">No deals available for this product</div>
                </div>
              )}
            </div>
            
            <div className="deal-popup-footer">
              <button 
                className="deal-close-button"
                onClick={() => setShowDealPopup(false)}
              >
                <div className="deal-close-button-circle"></div>
                <span className="deal-close-button-text">Close</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cart Error Popup */}
      {showErrorPopup && (
        <>
          <div className="deal-popup-overlay" onClick={() => {
            setShowErrorPopup(false);
            setIsInventoryError(false);
            setDismissedErrorAt(Date.now());
          }}>
            <div className="deal-popup error-popup" onClick={(e) => e.stopPropagation()}>
              <div className={`deal-popup-header ${isInventoryError ? 'inventory-popup-header' : 'error-popup-header'}`}>
                <h3>{isInventoryError ? 'Out of Stock' : 'Cart Error'}</h3>
              </div>
              
              <div className="deal-popup-content error-popup-content">
                <p>{errorMessage}</p>
              </div>
              
              <div className="deal-popup-footer error-popup-footer">
                <button 
                  className="deal-close-button error-view-cart-btn"
                  onClick={() => {
                    setShowErrorPopup(false);
                    setIsInventoryError(false);
                    setDismissedErrorAt(Date.now());
                    toggleCart();
                  }}
                >
                  VIEW CART
                </button>
                <button 
                  className="deal-close-button error-dismiss-btn"
                  onClick={() => {
                    setShowErrorPopup(false);
                    setIsInventoryError(false);
                    setDismissedErrorAt(Date.now());
                  }}
                >
                  DISMISS
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* Product Detail Drawer */}
      <ProductDetailDrawer
        key={selectedProduct?.id}
        product={selectedProduct}
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        menuId={menuId}
        allProducts={products}
        pricingType={initialPricingType}
        cartProp={cart}
        cartLoading={cartLoading}
        addingToCart={addingToCart}
        activeMarquees={activeMarquees}
        onShowError={(message: string) => {
          setErrorMessage(message);
          setIsInventoryError(false);
          setShowErrorPopup(true);
        }}
        stoppingMarquees={stoppingMarquees}
        dealDescriptions={dealDescriptions}
        productTileCursorStyle={{ cursor: 'pointer' }}
        isMobileProp={isMobile}
        selectedTerpenes={selectedTerpenes}
        selectedCannabinoids={selectedCannabinoids}
        onProductClick={handleProductTileClick}
        onProductHover={handleProductTileHover}
        onProductLeave={handleProductTileLeave}
        onAddToCart={handleAddToCart}
        onDebugClick={handleDebugClick}
        onDealClick={handleDealClick}
        onVermontIncrement={handleVermontIncrement}
        onVermontDecrement={handleVermontDecrement}
      />
    </div>
    </>
  );
}

export default React.memo(DutchieMenu);